import { useState, useEffect, useRef, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import confetti from "canvas-confetti";
import { useBookingOrchestration, type BookingResult, type DuplicateCandidate, type DoubleBookingConflict, type CalendarPushFailure } from "@/hooks/use-booking-orchestration";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea, VoiceToTextButton } from "@/components/voice-field";
import { RichTextEditor, type RichTextEditorHandle } from "@/components/rich-text-editor";
import { CcEmailInput } from "@/components/cc-email-input";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EventbriteEventPicker } from "@/components/eventbrite-event-picker";
import { FileText, Loader2, MapPin, Clock, DollarSign, Calendar, Globe, Plus, CheckCircle2, Mail, Send, PartyPopper, Upload, Download, Trash2, ChevronDown, ChevronRight, Settings2, Pencil, Users } from "lucide-react";
import { apiRequest, withCsrfHeader } from "@/lib/queryClient";
import { getWorkspaceTimeZone } from "@/lib/utils";
import { toEventDate } from "@shared/event-date";
import { useToast } from "@/hooks/use-toast";
import { EmailAttachmentsField, type StagedAttachment } from "@/components/email-attachments-field";
import { useSendEmailWithPreflight } from "@/hooks/use-pending-email-drafts";
import { AdvancedBookingMatrixDialog } from "@/components/AdvancedBookingMatrixDialog";
import { useAuth } from "@/hooks/use-auth";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AddressAutocomplete } from "./address-autocomplete";
import { EmailComposerModal } from "@/components/email-composer-modal";
import { ScheduledEmailDialog } from "@/components/scheduled-email-dialog";
import { CatchMeUpButton } from "@/components/catch-me-up-button";
import { MeetingCard, type MeetingRecord } from "@/pages/calendars";
import type { Client, Region, Invoice, ClientContact } from "@shared/schema";

// IMPORTANT: keep ALL `import` statements above this line. Splitting imports
// across non-import code (interfaces, consts, even just a stray statement)
// triggers a real TDZ in Rollup/esbuild production builds — see commit
// 24dc6f39 ("Fix error preventing leads from being viewed") and the May-17-2026
// re-occurrence that broke /leads, /clients and /invoices simultaneously.
// A safeguard now runs in `script/check-imports-first.ts` (wired into the
// build) and `npm run check:imports` to fail fast if anyone re-introduces
// the anti-pattern.

interface EventOffering {
  id: string;
  name: string;
  rate: string | null;
  minutesBefore: number;
  minutesAfter: number;
  isDefault: boolean;
  sortOrder: number;
  talentId: string | null;
  hasDynamicPricing?: boolean;
  noSetRate?: boolean;
}

interface Talent {
  id: string;
  name: string;
  isDefault: boolean;
  isActive: boolean;
}

interface ClientTypeRecord {
  id: string;
  name: string;
  isActive: boolean | null;
  sortOrder: number | null;
  isVenueStyle?: boolean | null;
}

interface RepOption {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
}

const STATE_TIMEZONE_MAP: Record<string, string> = {
  'CT': 'America/New_York', 'DE': 'America/New_York', 'DC': 'America/New_York',
  'FL': 'America/New_York', 'GA': 'America/New_York', 'IN': 'America/Indiana/Indianapolis',
  'KY': 'America/Kentucky/Louisville', 'ME': 'America/New_York', 'MD': 'America/New_York',
  'MA': 'America/New_York', 'MI': 'America/Detroit', 'NH': 'America/New_York',
  'NJ': 'America/New_York', 'NY': 'America/New_York', 'NC': 'America/New_York',
  'OH': 'America/New_York', 'PA': 'America/New_York', 'RI': 'America/New_York',
  'SC': 'America/New_York', 'VT': 'America/New_York', 'VA': 'America/New_York',
  'WV': 'America/New_York',
  'AL': 'America/Chicago', 'AR': 'America/Chicago', 'IL': 'America/Chicago',
  'IA': 'America/Chicago', 'KS': 'America/Chicago', 'LA': 'America/Chicago',
  'MN': 'America/Chicago', 'MS': 'America/Chicago', 'MO': 'America/Chicago',
  'NE': 'America/Chicago', 'ND': 'America/Chicago', 'OK': 'America/Chicago',
  'SD': 'America/Chicago', 'TN': 'America/Chicago', 'TX': 'America/Chicago',
  'WI': 'America/Chicago',
  'AZ': 'America/Phoenix', 'CO': 'America/Denver', 'ID': 'America/Boise',
  'MT': 'America/Denver', 'NM': 'America/Denver', 'UT': 'America/Denver',
  'WY': 'America/Denver',
  'CA': 'America/Los_Angeles', 'NV': 'America/Los_Angeles', 'OR': 'America/Los_Angeles',
  'WA': 'America/Los_Angeles',
  'AK': 'America/Anchorage', 'HI': 'Pacific/Honolulu',
};

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Arizona (MT - no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
];

function getTimezoneForState(state?: string): string {
  if (!state) return 'America/Chicago';
  const stateUpper = state.toUpperCase().trim();
  return STATE_TIMEZONE_MAP[stateUpper] || 'America/Chicago';
}

const triggerConfetti = () => {
  const duration = 1667;
  const animationEnd = Date.now() + duration;
  const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };
  const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;
  const interval = setInterval(() => {
    const timeLeft = animationEnd - Date.now();
    if (timeLeft <= 0) return clearInterval(interval);
    const particleCount = 50 * (timeLeft / duration);
    confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
    confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
  }, 250);
};

interface ClientRecordDialogProps {
  client: Client | null;
  regions: Region[];
  isOpen: boolean;
  onClose: () => void;
  onDuplicate?: (newClient: Client) => void;
  userId: string;
}

interface AddEventDialogProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
  userId: string;
  onSuccess: (invoiceId?: string) => void;
}

// Canonical dynamic-rate resolver shared by the main client form and the
// "Book Another Event" dialog. Resolves all pricing tiers in priority order:
// region+clientType → allRegions+clientType → clientType-only → region-only →
// allRegions base → offering base.
function getDynamicRate(
  regionId: string | null | undefined,
  clientType: string | null | undefined,
  offering: any,
  marketPricing: any[],
): string {
  if (!offering) return "0";
  if (!offering.hasDynamicPricing) return offering.rate || "0";
  if (!regionId && !clientType) return offering.rate || "0";

  // 1. Most specific: exact region + client type
  let pricing = marketPricing.find((p: any) =>
    !p.appliesToAllRegions && p.regionId === regionId && p.clientType && p.clientType.toLowerCase() === clientType?.toLowerCase()
  );

  // 2. All-regions sentinel + matching client type (auto-applies to new regions)
  if (!pricing && clientType) {
    pricing = marketPricing.find((p: any) =>
      p.appliesToAllRegions === true && p.clientType && p.clientType.toLowerCase() === clientType?.toLowerCase()
    );
  }

  // 3. Fall back to client type only - find ANY entry that matches client type
  // This handles the case where region isn't set yet but client type is known
  if (!pricing && clientType) {
    const clientTypePricing = marketPricing.filter((p: any) =>
      p.clientType && p.clientType.toLowerCase() === clientType?.toLowerCase()
    );
    // If there are pricing entries for this client type, use the first one's rate
    // (Assumes all entries for same client type have same rate)
    if (clientTypePricing.length > 0) {
      pricing = clientTypePricing[0];
    }
  }

  // 4. Region-only pricing (clientType is null/empty, applies to all types)
  if (!pricing && regionId) {
    pricing = marketPricing.find((p: any) => !p.appliesToAllRegions && p.regionId === regionId && !p.clientType);
  }

  // 5. All-regions sentinel with no client type (applies to every region & every client type)
  if (!pricing) {
    pricing = marketPricing.find((p: any) => p.appliesToAllRegions === true && !p.clientType);
  }

  if (!pricing) return offering.rate || "0";
  return pricing.rate || offering.rate || "0";
}

function AddEventDialog({ isOpen, onClose, client, userId, onSuccess }: AddEventDialogProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClientHook = useQueryClient();
  const [, setLocation] = useLocation();
  const sendBookingEmail = useSendEmailWithPreflight();
  
  const { data: eventOfferings = [] } = useQuery<EventOffering[]>({
    queryKey: ["/api/event-offerings"],
  });

  const { data: talent = [] } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
  });

  const { data: repOptions = [] } = useQuery<RepOption[]>({
    queryKey: ["/api/rep-options"],
  });
  
  const hasMultipleTalent = talent.length > 1;
  const defaultTalent = talent.find(e => e.isDefault) || talent[0];
  
  const [matrixDialogOpen, setMatrixDialogOpen] = useState(false);
  const [doubleBookConflict, setDoubleBookConflict] = useState<DoubleBookingConflict | null>(null);
  const [calendarPushFailure, setCalendarPushFailure] = useState<CalendarPushFailure | null>(null);
  const [isRollingBack, setIsRollingBack] = useState(false);
  // Phase 3 "Lite mode" gate. Hide the Advanced settings link entirely
  // when the user lacks booking.feature.advancedSettings — and defensively
  // force isRecurring back to false on every render so a stale flag from
  // a previously-permissioned session can't leak into a submission.
  const { can: canPermission } = useEffectivePermissions();
  const canShowAdvancedSettings = canPermission("booking.feature.advancedSettings");
  // Defensive: if permission is revoked while the dialog is open, snap
  // any leftover recurring state back off so the booking proceeds as a
  // single show (the popover that toggled it is no longer reachable).
  useEffect(() => {
    if (!canShowAdvancedSettings) {
      setShowData((prev) =>
        prev.isRecurring ? { ...prev, isRecurring: false } : prev,
      );
    }
  }, [canShowAdvancedSettings]);
  const [showData, setShowData] = useState({
    rate: "",
    discount: "0",
    dateOfEvent: "",
    eventTime: "",
    eventOfferingId: "",
    eventDetails: "",
    talentId: "",
    rep: "",
    isTicketedEvent: false,
    isNoFeeShow: false,
    eventbriteEventId: "",
    // Task #698 — Venue deal-type flags mirrored from the client record so
    // the Add Show dialog can unlock Rate the same way the main edit view
    // does. Any changes are persisted back to the client on book.
    venueTicketSplit: !!(client as any)?.venueTicketSplit,
    venueGuarantee: !!(client as any)?.venueGuarantee,
    venueBuyout: !!(client as any)?.venueBuyout,
    venueRental: !!(client as any)?.venueRental,
    // Recurring Event / Multi-date — Phase 1 test toggle. When `isRecurring`
    // is true, after the booking orchestration succeeds we POST the just-
    // created invoice to /api/event-series-from-invoice/:id which mints the
    // event_series row and tags the anchor invoice with seriesId.
    isRecurring: false,
    cadence: "weekly" as "weekly" | "biweekly" | "monthly" | "every_n_days",
    cadenceN: 1,
    endsOn: "",
  });

  // Get offerings for the selected talent (or default talent if none selected)
  const activeTalentId = showData.talentId || defaultTalent?.id;
  const filteredOfferings = activeTalentId
    ? eventOfferings.filter(o => o.talentId === activeTalentId)
    : eventOfferings;
  
  // Find default offering from filtered offerings (prioritize isDefault, then "Full Show", then first)
  const defaultOffering = filteredOfferings.find(o => o.isDefault) 
    || filteredOfferings.find(o => o.name.toLowerCase().includes("full show"))
    || filteredOfferings[0];
  
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [createdInvoiceId, setCreatedInvoiceId] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [showPreBookingEmailDialog, setShowPreBookingEmailDialog] = useState(false);
  const [preBookingEmailSubject, setPreBookingEmailSubject] = useState("");
  const [preBookingEmailBody, setPreBookingEmailBody] = useState("");
  const preBookingEmailBodyEditorRef = useRef<RichTextEditorHandle>(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isBooking, setIsBooking] = useState(false);

  useEffect(() => {
    // Only initialize form data when dialog first opens and data is loaded
    // Wait for repOptions to load to properly set the default rep
    if (isOpen && !hasInitialized && eventOfferings.length > 0 && repOptions.length > 0) {
      const initTalentId = defaultTalent?.id || "";
      // Get offerings for default talent
      const talentOfferings = initTalentId
        ? eventOfferings.filter(o => o.talentId === initTalentId)
        : eventOfferings;
      // Find the best default offering
      const initOffering = talentOfferings.find(o => o.isDefault)
        || talentOfferings.find(o => o.name.toLowerCase().includes("full show"))
        || talentOfferings[0];
      
      // Find the current user in rep options to set as default - match by user ID for accuracy
      const currentUserRep = repOptions.find(r => r.id === user?.id);
      // Use first available rep option as fallback (only if current user not found)
      const defaultRepName = currentUserRep?.fullName || repOptions[0]?.fullName || "";
      
      setShowData({
        rate: initOffering?.rate || "0",
        discount: "0",
        dateOfEvent: "",
        eventTime: "",
        eventOfferingId: initOffering?.id || "",
        eventDetails: initOffering?.name || "",
        talentId: initTalentId,
        rep: defaultRepName,
        isTicketedEvent: false,
        isNoFeeShow: false,
        venueTicketSplit: !!(client as any)?.venueTicketSplit,
        venueGuarantee: !!(client as any)?.venueGuarantee,
        venueBuyout: !!(client as any)?.venueBuyout,
        venueRental: !!(client as any)?.venueRental,
        isRecurring: false,
        cadence: "weekly",
        cadenceN: 1,
        endsOn: "",
      });
      setHasInitialized(true);
    }
    // Reset initialization flag when dialog closes
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen, eventOfferings, defaultTalent, hasInitialized, repOptions, user?.id]);
  
  const calculatedTotal = Math.max(0, parseFloat(showData.rate || "0") - parseFloat(showData.discount || "0"));

  const selectedOffering = eventOfferings.find(o => o.id === showData.eventOfferingId);
  
  // Fetch market pricing for selected offering (if dynamic pricing enabled)
  const { data: showDataMarketPricing = [] } = useQuery<any[]>({
    queryKey: ["/api/event-offerings", showData.eventOfferingId, "market-pricing"],
    enabled: !!showData.eventOfferingId && !!selectedOffering?.hasDynamicPricing,
  });
  
  // Update rate when market pricing data is fetched (for dynamic pricing in Log Event form).
  // Uses the canonical getDynamicRate resolver so the rebook dialog matches the main client form.
  useEffect(() => {
    if (!isOpen || showData.isTicketedEvent || showData.isNoFeeShow) return;
    // "No Set Rate" offerings: never auto-overwrite — user enters rate manually each time
    if ((selectedOffering as any)?.noSetRate) return;
    if (!selectedOffering?.hasDynamicPricing) return;
    if (showDataMarketPricing.length === 0) return;

    const newRate = getDynamicRate(client.regionId, client.clientType, selectedOffering, showDataMarketPricing);
    if (newRate !== showData.rate) {
      setShowData(prev => ({ ...prev, rate: newRate }));
    }
  }, [showDataMarketPricing, client.regionId, client.clientType, selectedOffering?.hasDynamicPricing, isOpen]);

  const createCalendarEventMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/calendar/booking", {
        clientId: client.id,
        accountName: client.accountName,
        address: client.address,
        city: client.city,
        state: client.state,
        zipCode: client.zipCode,
        timezone: (client as any).timezone || getTimezoneForState(client.state || ""),
        dateOfEvent: showData.dateOfEvent,
        eventTime: showData.eventTime,
        eventDetails: showData.eventDetails,
        contactName: client.contactName,
        phoneNumber: client.phoneNumber,
        calendarNotes: (client as any).calendarNotes || "",
        minutesBefore: selectedOffering?.minutesBefore ?? 30,
        minutesAfter: selectedOffering?.minutesAfter ?? 60,
        talentId: showData.talentId || (client as any).talentId || undefined,
      });
    },
    onSuccess: () => {
      toast({ title: "Calendar Event Created", description: "Appointment added to calendar." });
    },
    onError: (error: any) => {
      console.error("Calendar event error:", error);
      toast({ title: "Calendar Warning", description: "Invoice created but calendar event failed.", variant: "default" });
    },
  });

  const createInvoiceMutation = useMutation({
    mutationFn: async () => {
      const fullAddress = [client.address, client.city, client.state, client.zipCode]
        .filter(Boolean)
        .join(", ");
      const response = await apiRequest("POST", "/api/invoices", {
        clientId: client.id,
        accountName: client.accountName,
        accountAddress: fullAddress,
        eventRate: showData.rate,
        discount: showData.discount,
        // Task #112 — pass the canonical YYYY-MM-DD straight through.
        dateOfEvent: showData.dateOfEvent || null,
        bookingDetails: showData.eventDetails,
        createdBy: userId,
        isTicketedEvent: showData.isTicketedEvent,
        isNoFeeShow: showData.isNoFeeShow,
        eventbriteEnabled: showData.isTicketedEvent && !!showData.eventbriteEventId,
        eventbriteEventId: showData.isTicketedEvent && showData.eventbriteEventId
          ? showData.eventbriteEventId
          : null,
      });
      return response.json();
    },
  });

  // Orchestration hook for unified booking flow
  const handleOrchestrationSuccess = async (result: BookingResult) => {
    console.log("[AddEventDialog] Orchestration success:", result);
    
    // Invalidate queries
    queryClientHook.invalidateQueries({ queryKey: ["/api/invoices"] });
    queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });

    // Recurring Event / Multi-date — Phase 1 test toggle. Fire the series
    // create endpoint AFTER the anchor invoice exists. Failure here is
    // surfaced as a non-blocking toast so the booking itself is preserved.
    // Phase 3 hard-stop (architect HIGH): even if local state somehow
    // says isRecurring, never call the recurring API when the user
    // lacks the Advanced Settings permission. Server also enforces this
    // but defending in depth keeps the UX from sending a request that
    // will just 403 back.
    if (showData.isRecurring && result.data?.invoice?.id && canShowAdvancedSettings) {
      try {
        await apiRequest("POST", `/api/event-series-from-invoice/${result.data.invoice.id}`, {
          cadence: showData.cadence,
          cadenceN: showData.cadenceN,
          endsOn: showData.endsOn || undefined,
          preflightDays: 3,
          autoSendInvoice: true,
          stopOnUnpaid: false,
          billingMode: "per_show",
        });
        toast({
          title: "Recurring series created",
          description: showData.endsOn
            ? `Series will run until ${new Date(showData.endsOn).toLocaleDateString()}.`
            : `Series will run on a ${showData.cadence} cadence until cancelled.`,
        });
      } catch (err: any) {
        console.error("[AddEventDialog] event-series create failed:", err);
        toast({
          title: "Series create failed",
          description: "The booking was created but the recurring schedule didn't attach. You can retry from the invoice.",
          variant: "destructive",
        });
      }
    }

    toast({ 
      title: "Show Booked!", 
      description: `Invoice #${result.data?.invoice.invoiceNumber} has been generated.` 
    });
    
    triggerConfetti();
    
    // Close and redirect
    onSuccess(result.data?.invoice.id);
    onClose();
    setLocation(`/invoices?id=${result.data?.invoice.id}`);
  };

  const { orchestrate, isPending: isOrchestrating } = useBookingOrchestration({
    onSuccess: handleOrchestrationSuccess,
    onDuplicatesFound: (duplicates) => {
      console.warn("[AddEventDialog] Unexpected duplicates:", duplicates);
    },
    onDoubleBookingConflict: (conflict) => setDoubleBookConflict(conflict),
    onCalendarPushFailed: (failure) => setCalendarPushFailure(failure),
  });

  const handleCalendarPushBookAnyways = () => {
    if (!calendarPushFailure) return;
    queryClientHook.invalidateQueries({ queryKey: ["/api/invoices"] });
    queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
    toast({
      title: "Show Booked (no calendar)",
      description: `Calendar could not be updated for ${calendarPushFailure.talentName}. Please reconnect their calendar in Admin Panel > Manage Talent.`,
      variant: "default",
    });
    onSuccess(calendarPushFailure.invoiceId);
    setCalendarPushFailure(null);
    onClose();
  };

  const handleCalendarPushCancel = async () => {
    if (!calendarPushFailure) return;
    setIsRollingBack(true);
    try {
      await apiRequest("POST", "/api/bookings/rollback", {
        clientId: calendarPushFailure.clientId,
        invoiceId: calendarPushFailure.invoiceId,
        // isNewClient=true → rollback deletes both invoice + client.
        // isNewClient=false (re-book) → rollback deletes invoice only; the
        // existing client record must not be touched.
        isNewClient: calendarPushFailure.isNewClient,
        // Include workspace-fallback calendar event so rollback can delete it.
        ...(calendarPushFailure.calendarEventId ? {
          calendarEventId: calendarPushFailure.calendarEventId,
          calendarTalentId: calendarPushFailure.talentId || undefined,
        } : {}),
      });
      queryClientHook.invalidateQueries({ queryKey: ["/api/invoices"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({
        title: "Booking cancelled",
        description: "The booking was removed. Nothing was saved.",
      });
      // Only clear the failure state (and close the dialog) on successful rollback.
      setCalendarPushFailure(null);
    } catch (err: any) {
      toast({
        title: "Rollback failed",
        description: "Could not cancel the booking. You can try again or book anyway.",
        variant: "destructive",
      });
      // Leave the dialog open so the user can retry Cancel or choose Book anyways.
    } finally {
      setIsRollingBack(false);
    }
  };

  const runOrchestration = async () => {
    // Task #324 — talent is required on events. Block the save before it ever
    // reaches the orchestrator if no talent is assigned.
    if (!showData.talentId) {
      toast({
        title: "Talent required",
        description: "Please assign a talent before saving this event.",
        variant: "destructive",
      });
      return;
    }
    console.log("[AddEventDialog] Running orchestration for existing client:", client.id);
    console.log("[AddEventDialog] showData:", showData);
    console.log("[AddEventDialog] Client data:", {
      clientType: (client as any).clientType,
      regionId: (client as any).regionId,
      emailAddress: client.emailAddress,
    });
    
    await orchestrate({
      clientId: client.id,
      accountName: client.accountName,
      contactName: client.contactName || "",
      emailAddress: client.emailAddress || "",
      phoneNumber: client.phoneNumber || "",
      address: client.address || "",
      city: client.city || "",
      state: client.state || "",
      zipCode: client.zipCode || "",
      timezone: (client as any).timezone || getTimezoneForState(client.state || ""),
      clientType: (client as any).clientType || "",
      region: "", // Not needed for existing client
      talentId: showData.talentId,
      eventOfferingId: showData.eventOfferingId,
      eventDetails: showData.eventDetails,
      rate: showData.rate,
      discount: showData.discount,
      dateOfEvent: showData.dateOfEvent,
      eventTime: showData.eventTime,
      calendarNotes: (client as any).calendarNotes || "",
      notes: "",
      rep: showData.rep || (client as any).rep || "", // Use form rep first, then client rep as fallback
      isTicketedEvent: showData.isTicketedEvent,
      isNoFeeShow: showData.isNoFeeShow,
      eventbriteEnabled: showData.isTicketedEvent && !!showData.eventbriteEventId,
      eventbriteEventId: showData.isTicketedEvent && showData.eventbriteEventId
        ? showData.eventbriteEventId
        : null,
    });
  };

  const validateFields = (): boolean => {
    const missingFields: string[] = [];
    if (!showData.dateOfEvent?.trim()) missingFields.push("Date of Event");
    if (!showData.eventTime?.trim()) missingFields.push("Event Time");
    
    if (missingFields.length > 0) {
      toast({
        title: "Required Fields Missing",
        description: `Please fill in: ${missingFields.join(", ")}`,
        variant: "destructive",
      });
      return false;
    }

    // Task #698 — Ticketed Event requires exactly one venue deal type so
    // every settlement-after-event booking has a clear deal structure.
    if (
      showData.isTicketedEvent &&
      !showData.venueTicketSplit &&
      !showData.venueGuarantee &&
      !showData.venueBuyout &&
      !showData.venueRental
    ) {
      toast({
        title: "Pick a settlement structure",
        description: "Choose Ticket Split, Guarantee, Buyout, or Rental for this Ticketed Event.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  // Task #698 — Persist any change to the four venue deal-type flags back
  // to the client record so the next booking sees the same deal structure.
  const persistVenueDealTypeIfChanged = async () => {
    const current = {
      venueTicketSplit: !!(client as any)?.venueTicketSplit,
      venueGuarantee: !!(client as any)?.venueGuarantee,
      venueBuyout: !!(client as any)?.venueBuyout,
      venueRental: !!(client as any)?.venueRental,
    };
    const next = {
      venueTicketSplit: showData.venueTicketSplit,
      venueGuarantee: showData.venueGuarantee,
      venueBuyout: showData.venueBuyout,
      venueRental: showData.venueRental,
    };
    const changed = (Object.keys(next) as Array<keyof typeof next>).some(
      (k) => current[k] !== next[k],
    );
    if (!changed) return;
    try {
      await apiRequest("PATCH", `/api/clients/${client.id}`, next);
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
    } catch (err) {
      console.error("[AddEventDialog] Failed to persist venue deal type:", err);
    }
  };

  // Load pre-booking email preview when Book Event is clicked
  const loadPreBookingEmailPreview = async () => {
    setIsLoadingPreview(true);
    try {
      const response: any = await apiRequest("POST", "/api/email/confirmation/preview", {
        clientId: client.id,
        eventRate: showData.rate,
        discount: showData.discount,
        // Task #112 — canonical YYYY-MM-DD.
        dateOfEvent: showData.dateOfEvent || null,
        eventTime: showData.eventTime,
        bookingDetails: showData.eventDetails,
      });
      setPreBookingEmailSubject(response.subject || "");
      setPreBookingEmailBody(response.body || "");
      setRecipientEmail(response.recipientEmail || "");
      setRecipientName(response.recipientName || "");
      setShowPreBookingEmailDialog(true);
    } catch (error: any) {
      console.error("Failed to load email preview:", error);
      toast({ title: "Error", description: "Failed to load confirmation template.", variant: "destructive" });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Complete booking flow: create invoice → calendar event → send email
  const completeBookingFlow = async () => {
    if (isBooking) return;
    setIsBooking(true);
    
    try {
      // Step 1: Create the invoice
      const invoiceData = await createInvoiceMutation.mutateAsync();
      queryClientHook.invalidateQueries({ queryKey: ["/api/invoices"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      
      // Step 2: Create calendar event
      if (showData.dateOfEvent && showData.eventTime) {
        try {
          await createCalendarEventMutation.mutateAsync();
        } catch (calendarError) {
          console.error("Calendar event failed:", calendarError);
          // Continue with flow even if calendar fails
        }
      }
      
      // Step 3: Send confirmation email (preflight Gmail; queue as draft if disconnected)
      if (client.emailAddress && preBookingEmailSubject && preBookingEmailBody) {
        // Fire-and-forget through the preflight wrapper. If Gmail is connected
        // it sends immediately; if not, the draft is held and the user is
        // prompted to reconnect. The booking flow itself doesn't block on this.
        const recipient = recipientEmail;
        sendBookingEmail(
          {
            endpoint: `/api/email/confirmation/${invoiceData.id}`,
            payload: {
              subject: preBookingEmailSubject,
              body: preBookingEmailBody,
            },
            recipientEmail: recipient,
            subject: preBookingEmailSubject,
            description: "booking confirmation",
          },
          {
            onSent: () => {
              toast({ title: "Show Confirmed!", description: `Confirmation email sent to ${recipient}` });
            },
            onQueued: () => {
              // Hook already toasted "Email held as draft" — no extra toast needed.
            },
            onError: (emailError: any) => {
              console.error("Email send failed:", emailError);
              toast({ title: "Email Warning", description: "Invoice created but confirmation email failed.", variant: "default" });
            },
          },
        );
      } else {
        toast({ title: "Show Booked!", description: `Invoice #${invoiceData.invoiceNumber} has been generated.` });
      }
      
      // Step 4: Close dialogs and redirect
      setShowPreBookingEmailDialog(false);
      onSuccess(invoiceData.id);
      onClose();
      setLocation(`/invoices?id=${invoiceData.id}`);
      
    } catch (error: any) {
      console.error("Booking flow error:", error);
      const errorMessage = error?.error || error?.message || "Failed to complete booking.";
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    } finally {
      setIsBooking(false);
    }
  };

  const handleBookEvent = async () => {
    if (!validateFields()) return;

    // Task #698 — Persist venue deal-type flag changes back to the client
    // before booking so the next show inherits the same structure.
    await persistVenueDealTypeIfChanged();

    // Use unified orchestration for booking flow
    runOrchestration();
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Book Another Event</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-4">
          <p className="text-sm text-muted-foreground">
            Create a new invoice for <strong>{client.accountName}</strong>
          </p>
          
          {/* Booking Section */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Booking</h4>
            
            {hasMultipleTalent && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Talent</Label>
                <Select
                  value={showData.talentId}
                  onValueChange={(value) => {
                    const talentOfferings = eventOfferings.filter(o => o.talentId === value);
                    const defaultOff = talentOfferings.find(o => o.isDefault) 
                      || talentOfferings.find(o => o.name.toLowerCase().includes("full show"))
                      || talentOfferings[0];
                    setShowData(prev => ({ 
                      ...prev, 
                      talentId: value,
                      eventOfferingId: defaultOff?.id || "",
                      eventDetails: defaultOff?.name || "",
                      rate: defaultOff?.rate || "0"
                    }));
                  }}
                >
                  <SelectTrigger data-testid="select-new-talent">
                    <SelectValue placeholder="Select talent" />
                  </SelectTrigger>
                  <SelectContent>
                    {talent.filter(e => e.isActive).map((talent) => (
                      <SelectItem key={talent.id} value={talent.id}>
                        {talent.name} {talent.isDefault && "(Default)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <Select
              value={showData.eventOfferingId}
              onValueChange={(value) => {
                const offering = filteredOfferings.find(o => o.id === value);
                // "No Set Rate" offerings: clear the rate field so the user MUST enter one
                let rate: string;
                if ((offering as any)?.noSetRate) {
                  rate = "";
                } else if (offering?.hasDynamicPricing) {
                  rate = getDynamicRate(client.regionId, client.clientType, offering, showDataMarketPricing);
                } else {
                  rate = offering?.rate || "0";
                }
                setShowData(prev => ({ 
                  ...prev, 
                  eventOfferingId: value,
                  eventDetails: offering?.name || "",
                  rate: showData.isTicketedEvent || showData.isNoFeeShow ? "0" : rate
                }));
              }}
            >
              <SelectTrigger data-testid="select-new-show-details">
                <SelectValue>{showData.eventDetails || "Select event type"}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {filteredOfferings.map((offering) => {
                  // Show dynamic price if available, otherwise show base rate
                  const displayRate = offering.hasDynamicPricing 
                    ? getDynamicRate(client.regionId, client.clientType, offering, showDataMarketPricing)
                    : (offering.rate || "0");
                  return (
                    <SelectItem key={offering.id} value={offering.id}>
                      {offering.name} - ${parseFloat(displayRate).toFixed(2)}
                      {offering.hasDynamicPricing && " (Custom)"}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-muted-foreground text-xs">
                  <DollarSign className="w-3 h-3" />
                  Rate
                </Label>
                {(showData.isTicketedEvent || showData.venueTicketSplit || showData.venueGuarantee || showData.venueBuyout || showData.venueRental || (selectedOffering as any)?.noSetRate) ? (
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={showData.rate}
                    onChange={(e) => setShowData(prev => ({ ...prev, rate: e.target.value }))}
                    placeholder="0.00"
                    data-testid="input-new-rate"
                  />
                ) : (
                  <div className="h-9 px-3 flex items-center text-sm font-medium bg-muted/50 rounded-md border" data-testid="text-new-rate">
                    ${parseFloat(showData.rate || "0").toFixed(2)}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="newDiscount" className="text-xs">Discount</Label>
                <Input
                  id="newDiscount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={showData.discount}
                  onChange={(e) => setShowData(prev => ({ ...prev, discount: e.target.value }))}
                  placeholder="0.00"
                  data-testid="input-new-discount"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Total</Label>
                <div className="h-9 px-3 flex items-center text-sm font-semibold text-primary bg-primary/10 rounded-md border border-primary/20" data-testid="text-new-total">
                  ${calculatedTotal.toFixed(2)}
                </div>
              </div>
            </div>
            
            {/* Ticketed Event / No Fee checkboxes */}
            <div className="flex flex-col gap-3 mt-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="addEventTicketedEvent"
                  checked={showData.isTicketedEvent}
                  onCheckedChange={(checked) => {
                    setShowData(prev => ({
                      ...prev,
                      isTicketedEvent: !!checked,
                      isNoFeeShow: checked ? false : prev.isNoFeeShow,
                    }));
                  }}
                  data-testid="checkbox-add-ticketed-event"
                />
                <Label htmlFor="addEventTicketedEvent" className="text-sm cursor-pointer">
                  Ticketed Event - settlement after event
                </Label>
              </div>
              {showData.isTicketedEvent && (
                <EventbriteEventPicker
                  value={showData.eventbriteEventId || ""}
                  onChange={(id) => setShowData(prev => ({ ...prev, eventbriteEventId: id }))}
                />
              )}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="addEventNoFeeEvent"
                  checked={showData.isNoFeeShow}
                  onCheckedChange={(checked) => {
                    setShowData(prev => ({
                      ...prev,
                      isNoFeeShow: !!checked,
                      isTicketedEvent: checked ? false : prev.isTicketedEvent,
                      rate: checked ? "0" : prev.rate,
                    }));
                  }}
                  data-testid="checkbox-add-no-fee-show"
                />
                <Label htmlFor="addEventNoFeeEvent" className="text-sm cursor-pointer">
                  No fee - comp / promo event
                </Label>
              </div>
              {/* Task #698 — Venue deal types (mutually exclusive). Selecting
                  any one unlocks the Rate field. Required when Ticketed Event
                  is checked so every settlement-after-event booking has a
                  clear deal structure. */}
              <div className="flex flex-wrap gap-x-4 gap-y-2 pt-1">
                {([
                  ["venueTicketSplit", "Ticket Split", "checkbox-add-venue-ticket-split"],
                  ["venueGuarantee", "Guarantee", "checkbox-add-venue-guarantee"],
                  ["venueBuyout", "Buyout", "checkbox-add-venue-buyout"],
                  ["venueRental", "Rental", "checkbox-add-venue-rental"],
                ] as const).map(([key, label, testId]) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`addEvent_${key}`}
                      checked={(showData as any)[key]}
                      onCheckedChange={(checked) => {
                        setShowData(prev => ({
                          ...prev,
                          venueTicketSplit: false,
                          venueGuarantee: false,
                          venueBuyout: false,
                          venueRental: false,
                          [key]: !!checked,
                        }));
                      }}
                      data-testid={testId}
                    />
                    <Label htmlFor={`addEvent_${key}`} className="text-sm cursor-pointer">
                      {label}
                    </Label>
                  </div>
                ))}
              </div>
              {showData.isTicketedEvent && !showData.venueTicketSplit && !showData.venueGuarantee && !showData.venueBuyout && !showData.venueRental && (
                <p className="text-xs text-destructive" data-testid="text-add-venue-deal-required">
                  Pick a settlement structure: Ticket Split, Guarantee, Buyout, or Rental.
                </p>
              )}
            </div>
          </div>
          
          {/* Schedule Section */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Schedule</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="newDateOfEvent" className="flex items-center gap-2">
                  <Calendar className="w-3 h-3" />
                  Date of Event <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="newDateOfEvent"
                  type="date"
                  value={showData.dateOfEvent}
                  onChange={(e) => setShowData(prev => ({ ...prev, dateOfEvent: e.target.value }))}
                  data-testid="input-new-date-of-show"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newShowTime" className="flex items-center gap-2">
                  <Clock className="w-3 h-3" />
                  Event Time <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="newShowTime"
                  type="text"
                  value={showData.eventTime}
                  onChange={(e) => setShowData(prev => ({ ...prev, eventTime: e.target.value }))}
                  placeholder="e.g. 2:00p"
                  data-testid="input-new-show-time"
                />
              </div>
            </div>
          </div>

          {/* Phase 3: Advanced settings link (gated by booking.feature.advancedSettings).
              When the user lacks the permission, the link is hidden entirely and
              isRecurring stays false — keeping the Book Show form simple. The popup
              contains the Recurring toggle and the multi-date / tour matrix entry. */}
          {canShowAdvancedSettings && (
            <div className="pt-2 border-t flex items-center justify-end">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground hover:text-foreground"
                    data-testid="button-advanced-booking-settings"
                  >
                    <Settings2 className="w-3.5 h-3.5 mr-1.5" />
                    Advanced settings
                    {showData.isRecurring && (
                      <Badge variant="secondary" className="ml-2 text-[10px] px-1.5 py-0">
                        Recurring on
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-96 p-4" align="end">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold mb-1">Advanced booking</h4>
                      <p className="text-xs text-muted-foreground">
                        Use these only when the booking spans multiple dates, talents, or repeats.
                      </p>
                    </div>

                    {/* Recurring toggle */}
                    <div className="space-y-3 border-t pt-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="addEventIsRecurring"
                          checked={showData.isRecurring}
                          onCheckedChange={(checked) =>
                            setShowData((prev) => ({ ...prev, isRecurring: !!checked }))
                          }
                          data-testid="checkbox-add-recurring"
                        />
                        <Label htmlFor="addEventIsRecurring" className="text-sm cursor-pointer">
                          Make this a recurring event / multi-date
                        </Label>
                      </div>
                      {showData.isRecurring && (
                        <div className="grid grid-cols-2 gap-3 pl-6">
                          <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Cadence</Label>
                            <Select
                              value={showData.cadence}
                              onValueChange={(value) =>
                                setShowData((prev) => ({ ...prev, cadence: value as any }))
                              }
                            >
                              <SelectTrigger data-testid="select-recurring-cadence">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="weekly">Weekly</SelectItem>
                                <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">
                              Ends on (optional)
                            </Label>
                            <Input
                              type="date"
                              value={showData.endsOn}
                              onChange={(e) =>
                                setShowData((prev) => ({ ...prev, endsOn: e.target.value }))
                              }
                              placeholder="No end date"
                              data-testid="input-recurring-ends-on"
                            />
                          </div>
                          <p className="col-span-2 text-xs text-muted-foreground">
                            Leave the end date blank for an indefinite Recurring Event. Set an end
                            date for a finite Multi-date series. Future invoices auto-send 3 days
                            before show time.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Multi-date / tour matrix launcher */}
                    <div className="border-t pt-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => setMatrixDialogOpen(true)}
                        data-testid="button-open-matrix-dialog"
                      >
                        Plan multi-date / tour…
                      </Button>
                      <p className="text-xs text-muted-foreground mt-2">
                        Build the whole series in one grid: per-date venue, time, rate, and
                        per-talent splits.
                      </p>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        {/* Total and Book Button */}
        <div className="space-y-3 pt-4 border-t">
          <div className="flex items-center justify-between px-3 py-2 bg-muted/30 rounded-lg">
            <span className="text-sm font-medium text-muted-foreground">Booking Total</span>
            <span className="text-lg font-bold text-primary" data-testid="text-booking-total">${calculatedTotal.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleBookEvent}
              disabled={isLoadingPreview || isOrchestrating}
              data-testid="button-book-new-show"
            >
              {(isLoadingPreview || isOrchestrating) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <FileText className="w-4 h-4 mr-2" />
              Book Show
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    
    {/* Pre-Booking Email Confirmation Dialog - shows BEFORE invoice creation */}
    <Dialog open={showPreBookingEmailDialog} onOpenChange={(open) => {
      if (!open && !isBooking && !isOrchestrating) {
        setShowPreBookingEmailDialog(false);
      }
    }}>
      <DialogContent className="w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Confirm Show Booking
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="bg-muted/50 p-3 rounded-md text-sm">
            <span className="text-muted-foreground">To: </span>
            <span className="font-medium">{recipientName}</span>
            <span className="text-muted-foreground"> &lt;{recipientEmail}&gt;</span>
          </div>

          <div className="space-y-2">
            <Label htmlFor="preBookingEmailSubject">Subject</Label>
            <Input
              id="preBookingEmailSubject"
              value={preBookingEmailSubject}
              onChange={(e) => setPreBookingEmailSubject(e.target.value)}
              placeholder="Email subject"
              data-testid="input-prebooking-email-subject"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="preBookingEmailBody">Message</Label>
              <VoiceToTextButton onTranscript={(t) => preBookingEmailBodyEditorRef.current?.insertAtCursor(t)} />
            </div>
            <RichTextEditor
              ref={preBookingEmailBodyEditorRef}
              value={preBookingEmailBody}
              onChange={setPreBookingEmailBody}
              placeholder="Email body"
              minHeight="250px"
              showMergeTokens={true}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button 
            variant="outline" 
            onClick={() => setShowPreBookingEmailDialog(false)}
            disabled={isBooking || isOrchestrating}
            data-testid="button-cancel-prebooking"
          >
            Cancel
          </Button>
          <Button
            onClick={completeBookingFlow}
            disabled={isBooking || isOrchestrating || !(preBookingEmailSubject && String(preBookingEmailSubject).trim()) || !(preBookingEmailBody && String(preBookingEmailBody).trim())}
            data-testid="button-confirm-show"
          >
            {(isBooking || isOrchestrating) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Send className="w-4 h-4 mr-2" />
            Confirm Show
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    
    {/* Email Confirmation Dialog - triggered after booking (legacy, can be removed) */}
    {createdInvoiceId && (
      <ConfirmationEmailDialog
        isOpen={showEmailDialog}
        onClose={() => {
          const invoiceIdToRedirect = createdInvoiceId;
          setShowEmailDialog(false);
          setCreatedInvoiceId(null);
          onClose();
          setLocation(`/invoices?id=${invoiceIdToRedirect}`);
        }}
        invoiceId={createdInvoiceId}
        onSuccess={() => {
          const invoiceIdToRedirect = createdInvoiceId;
          setShowEmailDialog(false);
          setCreatedInvoiceId(null);
          onClose();
          setLocation(`/invoices?id=${invoiceIdToRedirect}`);
        }}
      />
    )}
    <AdvancedBookingMatrixDialog
      open={matrixDialogOpen}
      onOpenChange={setMatrixDialogOpen}
      defaultClientId={client?.id}
      onCreated={() => {
        onClose();
        onSuccess?.();
      }}
    />

    {doubleBookConflict && (
      <AlertDialog open={true} onOpenChange={(o) => { if (!o) setDoubleBookConflict(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Double Booking Detected</AlertDialogTitle>
            <AlertDialogDescription>{doubleBookConflict.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDoubleBookConflict(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-force-book-confirm"
              onClick={() => {
                const pending = doubleBookConflict.pendingFormData;
                setDoubleBookConflict(null);
                orchestrate({ ...pending, forceTalentOverlap: true });
              }}
            >
              Book Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )}

    {/* Calendar push failure — blocking choice before close */}
    {calendarPushFailure && (
      <AlertDialog open={true} onOpenChange={() => {}}>
        <AlertDialogContent data-testid="dialog-calendar-push-failed">
          <AlertDialogHeader>
            <AlertDialogTitle>Calendar Unavailable</AlertDialogTitle>
            <AlertDialogDescription>
              The booking was created, but{" "}
              <strong>{calendarPushFailure.talentName}</strong>'s Google Calendar
              connection is no longer valid — the token was revoked or expired.
              <br /><br />
              You can <strong>book anyway</strong> (the invoice is saved, but no
              calendar event will be created) or <strong>cancel</strong> to undo
              the booking entirely and try again after reconnecting the calendar
              in <em>Admin Panel → Manage Talent → Connect Calendar</em>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="button-calendar-push-cancel"
              onClick={handleCalendarPushCancel}
              disabled={isRollingBack}
            >
              {isRollingBack ? "Cancelling…" : "Cancel booking"}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-calendar-push-book-anyways"
              onClick={handleCalendarPushBookAnyways}
              disabled={isRollingBack}
            >
              Book anyways
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )}
    </>
  );
}

interface ConfirmationEmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  onSuccess: () => void;
}

function ConfirmationEmailDialog({ isOpen, onClose, invoiceId, onSuccess }: ConfirmationEmailDialogProps) {
  const { toast } = useToast();
  const queryClientHook = useQueryClient();
  
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const bodyEditorRef = useRef<RichTextEditorHandle>(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [attachments, setAttachments] = useState<StagedAttachment[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setAttachments([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && invoiceId) {
      setIsLoading(true);
      apiRequest("GET", `/api/email/confirmation/${invoiceId}/preview`)
        .then((data: any) => {
          setSubject(data.subject || "");
          setBody(data.body || "");
          setRecipientEmail(data.recipientEmail || "");
          setRecipientName(data.recipientName || "");
        })
        .catch((error) => {
          console.error("Failed to load email preview:", error);
          toast({ title: "Error", description: "Failed to load email template.", variant: "destructive" });
        })
        .finally(() => setIsLoading(false));
    }
  }, [isOpen, invoiceId, toast]);

  const sendConfirmationEmail = useSendEmailWithPreflight();
  const [isSendingConfirmation, setIsSendingConfirmation] = useState(false);
  const [isScheduleConfirmationOpen, setIsScheduleConfirmationOpen] = useState(false);

  const sendEmailMutation = {
    isPending: isSendingConfirmation,
    mutate: async () => {
      // Snapshot fields, drop the dialog, then run preflight + send (or hold).
      const recipient = recipientEmail;
      const sub = subject;
      const payload: { subject: string; body: string; attachments?: { filename: string; contentBase64: string; contentType: string }[] } = { subject, body };
      if (attachments.length > 0) {
        payload.attachments = attachments.map((a) => ({
          filename: a.filename,
          contentBase64: a.contentBase64,
          contentType: a.contentType,
        }));
      }
      onClose();
      setIsSendingConfirmation(true);
      await sendConfirmationEmail(
        {
          endpoint: `/api/email/confirmation/${invoiceId}`,
          payload,
          recipientEmail: recipient,
          subject: sub,
          description: "booking confirmation",
        },
        {
          onSent: () => {
            queryClientHook.invalidateQueries({ queryKey: ["/api/invoices"] });
            toast({ title: "Email Sent!", description: `Confirmation email sent to ${recipient}` });
            triggerConfetti();
            onSuccess();
          },
          onQueued: () => {
            onSuccess();
          },
          onError: (error: any) => {
            console.error("Failed to send email:", error);
            const errorMessage = error?.error || error?.message || "Failed to send email.";
            toast({ title: "Error", description: errorMessage, variant: "destructive" });
          },
        },
      );
      setIsSendingConfirmation(false);
    },
  } as const;

  const handleSend = () => {
    if (!subject.trim() || !body.trim()) {
      toast({ title: "Missing Content", description: "Please enter a subject and body.", variant: "destructive" });
      return;
    }
    sendEmailMutation.mutate();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Send Confirmation Email
          </DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="bg-muted/50 p-3 rounded-md text-sm">
              <span className="text-muted-foreground">To: </span>
              <span className="font-medium">{recipientName}</span>
              <span className="text-muted-foreground"> &lt;{recipientEmail}&gt;</span>
            </div>

            <div className="space-y-2">
              <Label htmlFor="emailSubject">Subject</Label>
              <Input
                id="emailSubject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
                data-testid="input-email-subject"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="emailBody">Message</Label>
                <VoiceToTextButton onTranscript={(t) => bodyEditorRef.current?.insertAtCursor(t)} />
              </div>
              <RichTextEditor
                ref={bodyEditorRef}
                value={body}
                onChange={setBody}
                placeholder="Email body"
                minHeight="250px"
                showMergeTokens={true}
              />
            </div>

            <EmailAttachmentsField
              value={attachments}
              onChange={setAttachments}
              testIdPrefix="client-confirmation-attachments"
            />
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} data-testid="button-skip-email">
            Skip Email
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsScheduleConfirmationOpen(true)}
            disabled={sendEmailMutation.isPending || isLoading || !subject.trim() || !body.trim()}
            data-testid="button-schedule-confirmation-email"
          >
            <Clock className="w-4 h-4 mr-2" />
            Schedule send
          </Button>
          <Button
            onClick={handleSend}
            disabled={sendEmailMutation.isPending || isLoading || !subject.trim() || !body.trim()}
            data-testid="button-send-confirmation-email"
          >
            {sendEmailMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Send className="w-4 h-4 mr-2" />
            Confirm Show
          </Button>
        </div>
        <ScheduledEmailDialog
          open={isScheduleConfirmationOpen}
          onOpenChange={setIsScheduleConfirmationOpen}
          sourceType="one_off"
          recipientSummary={
            recipientEmail ? `Schedule confirmation to ${recipientEmail}` : undefined
          }
          subjectSummary={subject}
          buildPayload={() => {
            if (!recipientEmail || !subject.trim() || !body.trim()) return null;
            return {
              to: recipientEmail,
              subject,
              htmlBody: body,
              attachments:
                attachments.length > 0
                  ? attachments.map((a) => ({
                      filename: a.filename,
                      contentBase64: a.contentBase64,
                      contentType: a.contentType,
                    }))
                  : undefined,
              invoiceId,
            };
          }}
          onScheduled={() => {
            // Mirror the immediate-send happy path: invalidate invoice
            // queries, fire confetti, close, and bubble onSuccess so the
            // parent treats the show as confirmed.
            queryClientHook.invalidateQueries({ queryKey: ["/api/invoices"] });
            triggerConfetti();
            onClose();
            onSuccess();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

// ── Additional Contacts Panel ─────────────────────────────────────────────────
interface ContactFormState {
  id?: string;
  name: string;
  title: string;
  phone: string;
  email: string;
}

const BLANK_FORM: ContactFormState = { name: "", title: "", phone: "", email: "" };

function ClientContactsSection({ clientId }: { clientId?: string }) {
  const queryClientHook = useQueryClient();
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState<ContactFormState | null>(null);

  const { data: contacts = [], isLoading } = useQuery<ClientContact[]>({
    queryKey: ["/api/clients", clientId, "contacts"],
    queryFn: () => apiRequest("GET", `/api/clients/${clientId}/contacts`).then(r => r.json()),
    enabled: !!clientId,
  });

  const invalidate = () => queryClientHook.invalidateQueries({ queryKey: ["/api/clients", clientId, "contacts"] });

  const createMutation = useMutation({
    mutationFn: (data: Omit<ContactFormState, "id">) =>
      apiRequest("POST", `/api/clients/${clientId}/contacts`, data),
    onSuccess: () => { invalidate(); setForm(null); },
    onError: () => toast({ title: "Error", description: "Failed to save contact", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: ContactFormState) =>
      apiRequest("PATCH", `/api/clients/${clientId}/contacts/${id}`, data),
    onSuccess: () => { invalidate(); setForm(null); },
    onError: () => toast({ title: "Error", description: "Failed to update contact", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/clients/${clientId}/contacts/${id}`),
    onSuccess: () => invalidate(),
    onError: () => toast({ title: "Error", description: "Failed to delete contact", variant: "destructive" }),
  });

  const handleSave = () => {
    if (!form || !form.name.trim()) return;
    if (form.id) {
      updateMutation.mutate(form);
    } else {
      const { id: _id, ...data } = form;
      createMutation.mutate(data);
    }
  };

  if (!clientId) return null;

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-1">
      {/* Toggle row */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="button-toggle-contacts"
        >
          {expanded ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
          <Users className="w-3 h-3" />
          <span>
            {contacts.length > 0 ? `Additional contacts (${contacts.length})` : "Additional contacts"}
          </span>
        </button>
        {!form && (
          <button
            type="button"
            onClick={() => { setExpanded(true); setForm(BLANK_FORM); }}
            className="text-xs text-primary hover:underline font-medium"
            data-testid="button-add-contact"
          >
            add contact +
          </button>
        )}
      </div>

      {/* Expanded list */}
      {expanded && (
        <div className="space-y-1 pl-2 border-l-2 border-muted ml-1">
          {isLoading && (
            <p className="text-xs text-muted-foreground py-1">Loading…</p>
          )}
          {!isLoading && contacts.length === 0 && !form && (
            <p className="text-xs text-muted-foreground py-1 italic">No additional contacts yet.</p>
          )}
          {contacts.map((c) => (
            <div key={c.id}>
              {form?.id === c.id ? (
                <ContactInlineForm
                  form={form}
                  onChange={setForm}
                  onSave={handleSave}
                  onCancel={() => setForm(null)}
                  isSaving={isSaving}
                />
              ) : (
                <div className="flex items-start justify-between group py-1" data-testid={`row-contact-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium">{c.name}</span>
                    {c.aiGenerated && (
                      <sup
                        className="text-[9px] leading-none text-muted-foreground/70 ml-0.5 cursor-help select-none"
                        title="AI-generated contact — not manually entered"
                        data-testid={`ai-flag-contact-${c.id}`}
                      >
                        *
                      </sup>
                    )}
                    {c.title && <span className="text-xs text-muted-foreground"> · {c.title}</span>}
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
                      {c.phone && (
                        <a href={`tel:${c.phone}`} className="hover:underline md:pointer-events-none md:cursor-default">{c.phone}</a>
                      )}
                      {c.email && <span>{c.email}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity ml-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setForm({ id: c.id, name: c.name, title: c.title || "", phone: c.phone || "", email: c.email || "" })}
                      className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                      data-testid={`button-edit-contact-${c.id}`}
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteMutation.mutate(c.id)}
                      className="text-xs text-destructive hover:text-destructive/80 flex items-center gap-0.5"
                      data-testid={`button-delete-contact-${c.id}`}
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* New-contact form (no id) */}
          {form && !form.id && (
            <ContactInlineForm
              form={form}
              onChange={setForm}
              onSave={handleSave}
              onCancel={() => setForm(null)}
              isSaving={isSaving}
            />
          )}

          {/* Add-another button — only visible when no form is open */}
          {!form && (
            <button
              type="button"
              onClick={() => setForm(BLANK_FORM)}
              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 pt-1"
              data-testid="button-add-another-contact"
            >
              <Plus className="w-3 h-3" /> Add contact
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ContactInlineForm({
  form,
  onChange,
  onSave,
  onCancel,
  isSaving,
}: {
  form: ContactFormState;
  onChange: (f: ContactFormState) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  return (
    <div className="space-y-2 py-2 pr-1">
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Name <span className="text-destructive">*</span></Label>
          <Input
            value={form.name}
            onChange={(e) => onChange({ ...form, name: e.target.value })}
            placeholder="Full name"
            className="h-7 text-sm"
            data-testid="input-contact-name-form"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Title / Role</Label>
          <Input
            value={form.title}
            onChange={(e) => onChange({ ...form, title: e.target.value })}
            placeholder="e.g. HR Director"
            className="h-7 text-sm"
            data-testid="input-contact-title-form"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Phone</Label>
          <Input
            value={form.phone}
            onChange={(e) => onChange({ ...form, phone: e.target.value })}
            placeholder="(555) 123-4567"
            className="h-7 text-sm"
            data-testid="input-contact-phone-form"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Email</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => onChange({ ...form, email: e.target.value })}
            placeholder="email@example.com"
            className="h-7 text-sm"
            data-testid="input-contact-email-form"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={isSaving || !form.name.trim()}
          className="text-xs bg-primary text-primary-foreground px-3 py-1 rounded hover:bg-primary/90 disabled:opacity-50"
          data-testid="button-save-contact"
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-muted-foreground hover:text-foreground px-2 py-1"
          data-testid="button-cancel-contact"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export function ClientRecordDialog({
  client,
  regions,
  isOpen,
  onClose,
  userId,
}: ClientRecordDialogProps) {
  const [, setLocation] = useLocation();
  
  const { data: eventOfferings = [] } = useQuery<EventOffering[]>({
    queryKey: ["/api/event-offerings"],
  });

  const { data: talent = [] } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
  });

  const { data: clientTypesData = [] } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
  });

  const clientTypeOptions = clientTypesData
    .filter(ct => ct.isActive)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  // Task #687 — "Smart-collapse Venue Details". Per-rep usage signal
  // tells us whether to start the section expanded automatically. Only
  // fetch while the dialog is actually open.
  const { data: venueUsage, isFetched: venueUsageFetched } = useQuery<{ frequent: boolean }>({
    queryKey: ["/api/me/venue-field-usage"],
    enabled: isOpen,
    staleTime: 5 * 60_000,
  });
  const isFrequentVenueUser = !!venueUsage?.frequent;

  // Pull workspace deposit defaults so the booking form pre-fills the
  // amount and so admins can lock the checkbox on with depositForcedByAdmin.
  const { data: invoiceSettingsForBooking } = useQuery<any>({
    queryKey: ["/api/invoice-settings"],
    staleTime: 60_000,
  });
  const depositSettings = invoiceSettingsForBooking || {};
  const depositsEnabledByAdmin = depositSettings.enableDeposits !== false;
  const depositForcedByAdmin = !!depositSettings.depositForcedByAdmin;
  const depositMode: "percent" | "flat" =
    depositSettings.depositMode === "flat" ? "flat" : "percent";
  const depositPercent = Number(depositSettings.depositPercentage ?? 50);
  const depositFlatCents = Number(depositSettings.depositFlatCents ?? 0);

  
  const hasMultipleTalent = talent.length > 1;
  const defaultTalent = talent.find(e => e.isDefault) || talent[0];
  const isNewClient = !client;
  
  // Track selected offering for dynamic pricing (use formData.eventOfferingId which updates as user selects)
  const [selectedOfferingIdForPricing, setSelectedOfferingIdForPricing] = useState<string>((client as any)?.eventOfferingId || "");
  
  // Fetch market pricing for the selected show offering (for dynamic pricing)
  const { data: mainFormMarketPricing = [] } = useQuery<any[]>({
    queryKey: ["/api/event-offerings", selectedOfferingIdForPricing, "market-pricing"],
    enabled: !!selectedOfferingIdForPricing && isOpen,
  });

  // Activity count for "Catch me up" gating (need >= 2 events to summarize)
  const { data: clientActivities = [] } = useQuery<any[]>({
    queryKey: ["/api/clients", client?.id, "activities"],
    queryFn: async () => {
      if (!client?.id) return [];
      const response = await fetch(`/api/clients/${client.id}/activities`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch activities");
      return response.json();
    },
    enabled: isOpen && !!client?.id,
  });
  

  const [formData, setFormData] = useState({
    accountName: client?.accountName || "",
    contactName: client?.contactName || "",
    clientType: (client as any)?.clientType || "",
    regionId: (client as any)?.regionId || "",
    phoneNumber: client?.phoneNumber || "",
    mobilePhone: (client as any)?.mobilePhone || "",
    emailAddress: client?.emailAddress || "",
    address: client?.address || "",
    city: client?.city || "",
    state: client?.state || "",
    zipCode: client?.zipCode || "",
    timezone: (client as any)?.timezone || getTimezoneForState(client?.state || ""),
    rate: client?.rate || "",
    discount: (client as any)?.discount || "0",
    // Task #112 — dateOfEvent is already canonical YYYY-MM-DD; the
    // helper accepts legacy ISO timestamps for safety during rollout.
    dateOfEvent: toEventDate(client?.dateOfEvent) ?? "",
    eventTime: client?.eventTime || "",
    eventDetails: client?.eventDetails || "",
    eventOfferingId: (client as any)?.eventOfferingId || "",
    talentId: (client as any)?.talentId || "",
    rep: (client as any)?.rep || "",
    notes: client?.notes || "",
    calendarNotes: (client as any)?.calendarNotes || "",
    isTicketedEvent: false,
    isNoFeeShow: false,
    eventbriteEventId: "",
    requiresDocuments: (client as any)?.requiresDocuments || false,
    depositRequired: false,
    depositAmount: "",
    venueType: (client as any)?.venueType || "",
    venueCapacity: (client as any)?.venueCapacity || "",
    venueRentalCost: (client as any)?.venueRentalCost || "",
    venueTicketPricing: (client as any)?.venueTicketPricing || "",
    venueTicketSplit: (client as any)?.venueTicketSplit || false,
    venueGuarantee: (client as any)?.venueGuarantee || false,
    venueBuyout: (client as any)?.venueBuyout || false,
    venueRental: (client as any)?.venueRental || false,
  });
  
  // Filter offerings by selected talent
  const activeTalentId = formData.talentId || defaultTalent?.id;
  const filteredOfferings = activeTalentId
    ? eventOfferings.filter(o => o.talentId === activeTalentId)
    : eventOfferings;
  
  // Find default offering from filtered offerings (prioritize isDefault, then "Full Show", then first)
  const defaultOffering = filteredOfferings.find(o => o.isDefault)
    || filteredOfferings.find(o => o.name.toLowerCase().includes("full show"))
    || filteredOfferings[0];
  
  // Task #687 — Smart-collapse Venue Details. `venueOpen` controls the
  // section; `venueUserToggled` latches once the user clicks the chevron
  // so subsequent client-type changes never override their choice.
  // `lastAutoOpenedForType` lets us auto-expand exactly once when the
  // user first picks a venue-style type during this dialog session.
  const [venueOpen, setVenueOpen] = useState(false);
  const [venueUserToggled, setVenueUserToggled] = useState(false);
  const [lastAutoOpenedForType, setLastAutoOpenedForType] = useState<string | null>(null);

  const [showAddEventDialog, setShowAddEventDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [pendingInvoiceId, setPendingInvoiceId] = useState<string | null>(null);
  const [showFirstBookingEmailDialog, setShowFirstBookingEmailDialog] = useState(false);
  const [firstBookingEmailSubject, setFirstBookingEmailSubject] = useState("");
  const [firstBookingEmailBody, setFirstBookingEmailBody] = useState("");
  const firstBookingEmailBodyEditorRef = useRef<RichTextEditorHandle>(null);
  const [firstBookingRecipientEmail, setFirstBookingRecipientEmail] = useState("");
  const [isLoadingFirstBookingPreview, setIsLoadingFirstBookingPreview] = useState(false);
  const [isFirstBooking, setIsFirstBooking] = useState(false);
  const isBookingInFlight = useRef(false);
  const [rawDoubleBookConflict, setRawDoubleBookConflict] = useState<{ message: string; talentConflicts: any[] } | null>(null);
  const [showEmailComposerModal, setShowEmailComposerModal] = useState(false);
  const [emailComposerInvoiceId, setEmailComposerInvoiceId] = useState<string | null>(null);
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false);
  const [calendarSaveFailure, setCalendarSaveFailure] = useState<{
    talentName: string;
    pendingUpdates: Partial<Client>;
  } | null>(null);
  const [docToDelete, setDocToDelete] = useState<any | null>(null);
  const [docToEmail, setDocToEmail] = useState<any | null>(null);
  const [docEmailTo, setDocEmailTo] = useState("");
  const [docEmailName, setDocEmailName] = useState("");
  const [docEmailCc, setDocEmailCc] = useState("");
  const { toast } = useToast();
  const queryClientHook = useQueryClient();

  const { data: allInvoices = [] } = useQuery<Invoice[]>({
    queryKey: ["/api/invoices"],
    enabled: isOpen,
  });

  // Fetch all clients for duplicate checking (only when creating new client)
  const { data: allClients = [] } = useQuery<Client[]>({
    queryKey: ["/api/clients", "lookup"],
    enabled: isOpen && isNewClient,
  });

  const clientInvoices = client ? allInvoices.filter(inv => inv.clientId === client.id) : [];

  const { data: clientDocuments = [] } = useQuery<any[]>({
    queryKey: ["/api/clients", client?.id, "documents"],
    enabled: isOpen && !!client?.id,
  });

  const deleteDocMutation = useMutation({
    mutationFn: async (docId: string) => {
      // Audit R16 — `apiRequest` is already statically imported on
      // line 127. The previous `await import(...)` here forced Vite
      // to keep `queryClient.ts` un-splittable (it's statically
      // imported by 152 other files), making the dynamic-import a
      // no-op. Switched to the static import.
      await apiRequest("DELETE", `/api/document-instances/${docId}`);
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients", client?.id, "documents"] });
      setDocToDelete(null);
      toast({ title: "Document deleted" });
    },
    onError: () => { toast({ title: "Failed to delete document", variant: "destructive" }); },
  });

  const emailDocMutation = useMutation({
    mutationFn: async ({ docId, toEmail, toName, cc }: { docId: string; toEmail: string; toName: string; cc?: string }) => {
      // Audit R16 — see deleteDocMutation; using the static import.
      await apiRequest("POST", `/api/document-instances/${docId}/email-document`, { toEmail, toName, cc });
    },
    onSuccess: () => {
      setDocToEmail(null);
      setDocEmailTo("");
      setDocEmailName("");
      setDocEmailCc("");
      toast({ title: "Document emailed successfully" });
    },
    onError: () => { toast({ title: "Couldn't send email", description: "Check your Gmail connection and try again.", variant: "destructive" }); },
  });

  function openDocumentFile(doc: any, forceDownload = false) {
    window.open(`/api/document-instances/${doc.id}/file${forceDownload ? "?download=true" : ""}`, "_blank");
  }

  // Task #687 — Smart-collapse Venue Details auto-open rules.
  // 1. On open, reset the manual-toggle latch + auto-open memo so the
  //    next dialog session starts fresh.
  // 2. Compute initial state: frequent venue users always start open;
  //    everyone else starts open only if the currently-selected client
  //    type is venue-style; otherwise collapsed.
  // 3. Mid-session client-type changes auto-open (once per type) when
  //    switching INTO a venue-style type and the user hasn't toggled
  //    manually yet. Switching to a non-venue type never auto-collapses.
  const isVenueStyleType = (typeName: string | null | undefined): boolean => {
    if (!typeName) return false;
    const match = clientTypesData.find(
      (ct) => ct.name?.toLowerCase() === typeName.toLowerCase(),
    );
    return !!match?.isVenueStyle;
  };

  // Tracks whether we've performed the one-shot auto-open computation
  // for the current dialog session. Once we have, async data arriving
  // later (usage query, client types) must NOT re-flip venueOpen and
  // override a manual toggle. Reset only when the dialog re-opens.
  const venueAutoInitDoneRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset all per-session state so a fresh open re-runs the
      // auto-open rule from scratch, even if this component instance
      // is reused (e.g. page-level dialog stays mounted across opens).
      venueAutoInitDoneRef.current = false;
      setVenueUserToggled(false);
      setLastAutoOpenedForType(null);
      return;
    }
    if (venueAutoInitDoneRef.current) return;
    // If the user has already clicked the chevron before async data
    // resolved, treat init as done and respect their explicit choice
    // — never overwrite a manual toggle.
    if (venueUserToggled) {
      venueAutoInitDoneRef.current = true;
      return;
    }
    // Wait until BOTH inputs the auto-open rule depends on have
    // actually resolved before locking in the initial state. Otherwise
    // a fast `/api/client-types` cache hit would let us finalize with
    // `isFrequentVenueUser=false` before the usage query returns,
    // permanently masking the frequent-user signal for this session.
    if (clientTypesData.length === 0) return;
    if (!venueUsageFetched) return;
    const initialType = formData.clientType || "";
    const startOpen = isFrequentVenueUser || isVenueStyleType(initialType);
    setVenueOpen(startOpen);
    setLastAutoOpenedForType(
      startOpen && isVenueStyleType(initialType) ? initialType : null,
    );
    venueAutoInitDoneRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isFrequentVenueUser, clientTypesData.length, venueUsageFetched, venueUserToggled]);

  useEffect(() => {
    if (!isOpen) return;
    if (!venueAutoInitDoneRef.current) return;
    if (venueUserToggled) return;
    const t = formData.clientType;
    if (!t) return;
    if (!isVenueStyleType(t)) return;
    if (lastAutoOpenedForType === t) return;
    setVenueOpen(true);
    setLastAutoOpenedForType(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.clientType, isOpen, venueUserToggled, clientTypesData.length]);

  // Check for duplicate client account name
  const checkForDuplicateClient = (accountName: string): boolean => {
    if (!accountName || !accountName.trim()) return false;
    const normalizedName = accountName.trim().toLowerCase();
    return allClients.some(c => c.accountName?.trim().toLowerCase() === normalizedName);
  };

  useEffect(() => {
    // Get the talent for this client
    const talentId = (client as any)?.talentId || defaultTalent?.id || "";
    // Get offerings for that talent
    const talentOfferings = talentId
      ? eventOfferings.filter(o => o.talentId === talentId)
      : eventOfferings;
    // Find the best default offering
    const initOffering = talentOfferings.find(o => o.isDefault)
      || talentOfferings.find(o => o.name.toLowerCase().includes("full show"))
      || talentOfferings[0];
    
    setFormData({
      accountName: client?.accountName || "",
      contactName: client?.contactName || "",
      clientType: (client as any)?.clientType || "",
      phoneNumber: client?.phoneNumber || "",
      mobilePhone: (client as any)?.mobilePhone || "",
      emailAddress: client?.emailAddress || "",
      address: client?.address || "",
      city: client?.city || "",
      state: client?.state || "",
      zipCode: client?.zipCode || "",
      timezone: (client as any)?.timezone || getTimezoneForState(client?.state || ""),
      rate: client?.rate || initOffering?.rate || "",
      discount: (client as any)?.discount || "0",
      // Task #112 — canonical YYYY-MM-DD; helper tolerates legacy ISO.
      dateOfEvent: toEventDate(client?.dateOfEvent) ?? "",
      eventTime: client?.eventTime || "",
      eventDetails: client?.eventDetails || initOffering?.name || "",
      eventOfferingId: (client as any)?.eventOfferingId || initOffering?.id || "",
      talentId: talentId,
      rep: (client as any)?.rep || "",
      notes: client?.notes || "",
      calendarNotes: (client as any)?.calendarNotes || "",
      isTicketedEvent: (client as any)?.isTicketedEvent || false,
      isNoFeeShow: (client as any)?.isNoFeeShow || false,
      eventbriteEventId: "",
      requiresDocuments: (client as any)?.requiresDocuments || false,
      depositRequired: false,
      depositAmount: "",
      venueType: (client as any)?.venueType || "",
      venueCapacity: (client as any)?.venueCapacity || "",
      venueRentalCost: (client as any)?.venueRentalCost || "",
      venueTicketPricing: (client as any)?.venueTicketPricing || "",
      venueTicketSplit: (client as any)?.venueTicketSplit || false,
      venueGuarantee: (client as any)?.venueGuarantee || false,
      venueBuyout: (client as any)?.venueBuyout || false,
      venueRental: (client as any)?.venueRental || false,
    });
  }, [client, eventOfferings, defaultTalent]);

  const updateClientMutation = useMutation({
    mutationFn: async (updates: Partial<Client> & { skipCalendarSync?: boolean }) => {
      if (!client) throw new Error("Cannot update a new client");
      const res = await apiRequest("PATCH", `/api/clients/${client.id}`, updates);
      // apiRequest throws on non-2xx, EXCEPT we need to inspect 422 ourselves.
      // Since apiRequest already throws for 422, catch it by re-fetching only on
      // error — instead, we patch the response check below in onError.
      return res;
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({ title: "Saved", description: "Client record updated." });
    },
    onError: async (error: any, variables) => {
      // apiRequest throws with format "422: {...JSON...}" for 422 responses.
      const raw: string = error?.message || "";
      if (raw.startsWith("422:")) {
        try {
          const body = JSON.parse(raw.slice(4).trim());
          if (body?.calendarSyncFailed) {
            setCalendarSaveFailure({
              talentName: body.talentName || "the talent",
              pendingUpdates: variables,
            });
            return; // Handled — don't show generic error toast.
          }
        } catch {}
      }
      toast({ title: "Error", description: "Failed to save changes.", variant: "destructive" });
    },
  });

  const createClientMutation = useMutation({
    mutationFn: async (clientData: any) => {
      return apiRequest("POST", "/api/clients", clientData);
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({ title: "Created", description: "New client record created." });
      onClose();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create client.", variant: "destructive" });
    },
  });

  const selectedOffering = eventOfferings.find(o => o.id === formData.eventOfferingId);
  
  // Update selectedOfferingIdForPricing when formData.eventOfferingId changes
  useEffect(() => {
    if (formData.eventOfferingId && formData.eventOfferingId !== selectedOfferingIdForPricing) {
      setSelectedOfferingIdForPricing(formData.eventOfferingId);
    }
  }, [formData.eventOfferingId]);
  
  // Update rate when market pricing data is fetched or client type changes
  useEffect(() => {
    if (!isOpen) return;
    if (formData.isTicketedEvent || formData.isNoFeeShow) return;
    if (!selectedOffering?.hasDynamicPricing) return;
    if (mainFormMarketPricing.length === 0) return;
    
    const newRate = getDynamicRate(formData.regionId || client?.regionId, formData.clientType, selectedOffering, mainFormMarketPricing);
    if (newRate !== formData.rate) {
      setFormData(prev => ({ ...prev, rate: newRate }));
    }
  }, [mainFormMarketPricing, formData.clientType, formData.regionId, selectedOffering?.hasDynamicPricing, isOpen]);
  
  const createCalendarEventMutation = useMutation({
    mutationFn: async () => {
      if (!client) throw new Error("Cannot create calendar event without client");
      return apiRequest("POST", "/api/calendar/booking", {
        clientId: client.id,
        accountName: formData.accountName,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        timezone: formData.timezone,
        dateOfEvent: formData.dateOfEvent,
        eventTime: formData.eventTime,
        eventDetails: formData.eventDetails,
        contactName: formData.contactName,
        phoneNumber: formData.phoneNumber,
        calendarNotes: formData.calendarNotes,
        minutesBefore: selectedOffering?.minutesBefore ?? 30,
        minutesAfter: selectedOffering?.minutesAfter ?? 60,
        talentId: formData.talentId || (client as any).talentId || undefined,
      });
    },
    onSuccess: () => {
      toast({ title: "Calendar Event Created", description: "Appointment added to calendar." });
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
    },
    onError: (error: any) => {
      console.error("Calendar event error:", error);
      toast({ title: "Calendar Warning", description: "Invoice created but calendar event failed.", variant: "default" });
    },
  });

  const createInvoiceMutation = useMutation({
    mutationFn: async () => {
      if (!client) throw new Error("Cannot create invoice without client");
      const fullAddress = [formData.address, formData.city, formData.state, formData.zipCode]
        .filter(Boolean)
        .join(", ");
      const depositRequired = depositForcedByAdmin
        ? true
        : !!(formData as any).depositRequired;
      const depositAmtRaw = (formData as any).depositAmount;
      const depositAmountCents = depositRequired && depositAmtRaw
        ? Math.round(parseFloat(String(depositAmtRaw)) * 100)
        : null;
      return apiRequest("POST", "/api/invoices", {
        clientId: client.id,
        accountName: formData.accountName,
        accountAddress: fullAddress,
        eventRate: formData.rate,
        // Task #112 — canonical YYYY-MM-DD straight through.
        dateOfEvent: formData.dateOfEvent || null,
        bookingDetails: formData.eventDetails,
        createdBy: userId,
        isTicketedEvent: formData.isTicketedEvent,
        isNoFeeShow: formData.isNoFeeShow,
        eventbriteEnabled: formData.isTicketedEvent && !!formData.eventbriteEventId,
        eventbriteEventId: formData.isTicketedEvent && formData.eventbriteEventId
          ? formData.eventbriteEventId
          : null,
        depositRequired,
        depositAmountCents,
        depositForcedByAdmin: depositForcedByAdmin && depositRequired,
      });
    },
    onSuccess: (data: any) => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/invoices"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({ title: "Show Booked!", description: `Invoice #${data.invoiceNumber} has been generated.` });
      triggerConfetti();
      
      if (formData.dateOfEvent && formData.eventTime) {
        createCalendarEventMutation.mutate();
      }
      
      // Open email dialog to preview/edit confirmation email
      if (data.id && formData.emailAddress) {
        setPendingInvoiceId(data.id);
        setShowEmailDialog(true);
      } else if (data.id) {
        onClose();
        setLocation(`/invoices?id=${data.id}`);
      }
    },
    onError: (error: any) => {
      console.error("Invoice creation error:", error);
      const errorMessage = error?.error || error?.message || "Failed to create invoice.";
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    },
  });

  const validateRequiredFields = (): boolean => {
    const missingFields: string[] = [];
    if (!formData.address?.trim()) missingFields.push("Street Address");
    if (!formData.city?.trim()) missingFields.push("City");
    if (!formData.state?.trim()) missingFields.push("State");
    if (!formData.zipCode?.trim()) missingFields.push("Zip Code");
    if (!formData.dateOfEvent?.trim()) missingFields.push("Date of Event");
    if (!formData.eventTime?.trim()) missingFields.push("Event Time");
    // Task #324 — talent is required on events.
    if (!(formData.talentId || defaultTalent?.id)) missingFields.push("Talent");
    
    if (missingFields.length > 0) {
      toast({
        title: "Required Fields Missing",
        description: `Please fill in: ${missingFields.join(", ")}`,
        variant: "destructive",
      });
      return false;
    }

    // Task #698 — Ticketed Event requires a venue deal type so every
    // settlement-after-event booking has a clear deal structure.
    if (
      formData.isTicketedEvent &&
      !formData.venueTicketSplit &&
      !formData.venueGuarantee &&
      !formData.venueBuyout &&
      !formData.venueRental
    ) {
      toast({
        title: "Pick a settlement structure",
        description: "Choose Ticket Split, Guarantee, Buyout, or Rental for this Ticketed Event.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  // Actually perform the client creation
  const performClientCreate = () => {
    createClientMutation.mutate({
      ...formData,
      createdBy: userId,
      rate: formData.rate || undefined,
      // Task #112 — pass canonical YYYY-MM-DD; insert schema accepts string.
      dateOfEvent: formData.dateOfEvent || undefined,
    });
  };

  const handleSave = () => {
    if (!validateRequiredFields()) return;
    
    if (isNewClient) {
      // Check for duplicate account name before creating
      if (checkForDuplicateClient(formData.accountName)) {
        setDuplicateConfirmOpen(true);
        return;
      }
      // Create new client
      performClientCreate();
    } else {
      // Update existing client
      updateClientMutation.mutate({
        ...formData,
        rate: formData.rate || undefined,
        // Task #112 — canonical YYYY-MM-DD straight through.
        dateOfEvent: formData.dateOfEvent || undefined,
      });
    }
  };
  
  // Handle confirmed duplicate creation
  const handleConfirmedClientCreate = () => {
    setDuplicateConfirmOpen(false);
    performClientCreate();
  };

  // Load email preview for first booking
  const loadFirstBookingEmailPreview = async () => {
    if (!client || !formData.emailAddress) return;
    
    setIsLoadingFirstBookingPreview(true);
    try {
      // Find the confirmation template for this client type
      // Task #696: even though the API returns every template (manager
      // needs full visibility), the client-record dialog is a human-facing
      // picker and must filter out picker-hidden templates.
      const templatesRes = await apiRequest("GET", "/api/email-templates");
      const allTemplates: Array<{
        id: string;
        type?: string;
        clientType?: string;
        subject?: string;
        body?: string;
        hiddenFromPickers?: boolean;
      }> = await templatesRes.json();
      const templates = allTemplates.filter((t) => !t.hiddenFromPickers);
      const confirmationTemplate = templates.find((t) =>
        t.type === "confirmation" && t.clientType === client.clientType
      ) || templates.find((t) => t.type === "confirmation");
      
      if (!confirmationTemplate) {
        toast({ title: "No confirmation template found", variant: "destructive" });
        setIsLoadingFirstBookingPreview(false);
        return;
      }
      
      // Get template preview with variable substitution
      const previewRes = await apiRequest("POST", "/api/email-templates/preview", {
        templateId: confirmationTemplate.id,
        clientId: client.id,
      });
      const preview = await previewRes.json();
      
      setFirstBookingEmailSubject(preview.subject || confirmationTemplate.subject);
      setFirstBookingEmailBody(preview.body || confirmationTemplate.body);
      setFirstBookingRecipientEmail(formData.emailAddress);
      setShowFirstBookingEmailDialog(true);
    } catch (error) {
      console.error("Error loading email preview:", error);
      toast({ title: "Error loading email template", variant: "destructive" });
    } finally {
      setIsLoadingFirstBookingPreview(false);
    }
  };

  // Complete booking flow with email for first booking
  const completeFirstBookingFlow = async () => {
    if (!client || isBookingInFlight.current) return;
    isBookingInFlight.current = true;
    setIsFirstBooking(true);
    
    try {
      // First save the client data
      await apiRequest("PATCH", `/api/clients/${client.id}`, {
        ...formData,
        rate: formData.rate || undefined,
        // Task #112 — canonical YYYY-MM-DD straight through.
        dateOfEvent: formData.dateOfEvent || undefined,
      });
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      
      // Create the invoice
      const fullAddress = [formData.address, formData.city, formData.state, formData.zipCode]
        .filter(Boolean)
        .join(", ");
      
      const response = await apiRequest("POST", "/api/invoices", {
        clientId: client.id,
        accountName: formData.accountName,
        accountAddress: fullAddress,
        clientEmail: formData.emailAddress,
        clientPhone: formData.phoneNumber,
        talentId: formData.talentId || defaultTalent?.id,
        eventOfferingId: formData.eventOfferingId || defaultOffering?.id,
        eventRate: formData.rate?.replace(/[$,]/g, "") || "0",
        discount: formData.discount || "0",
        // Task #112 — canonical YYYY-MM-DD straight through; fallback today.
        dateOfEvent: formData.dateOfEvent || new Date().toISOString().slice(0, 10),
        bookingDetails: formData.eventDetails,
        rep: formData.rep,
        isTicketedEvent: formData.isTicketedEvent,
        isNoFeeShow: formData.isNoFeeShow,
        eventbriteEnabled: formData.isTicketedEvent && !!formData.eventbriteEventId,
        eventbriteEventId: formData.isTicketedEvent && formData.eventbriteEventId
          ? formData.eventbriteEventId
          : null,
      });
      const invoice = await response.json();
      
      // Send the confirmation email
      await apiRequest("POST", "/api/emails/send-confirmation", {
        invoiceId: invoice.id,
        recipientEmail: firstBookingRecipientEmail,
        subject: firstBookingEmailSubject,
        body: firstBookingEmailBody,
      });
      
      toast({ title: "Show Confirmed!", description: `Invoice #${invoice.invoiceNumber} created and confirmation email sent.` });
      
      queryClientHook.invalidateQueries({ queryKey: ["/api/invoices"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      setShowFirstBookingEmailDialog(false);
      
      // Auto-open the new invoice
      if (invoice.id) {
        setPendingInvoiceId(invoice.id);
      }
    } catch (error: any) {
      console.error("Error completing booking:", error);
      toast({ title: "Error", description: "Couldn't complete the booking. Please try again.", variant: "destructive" });
    } finally {
      isBookingInFlight.current = false;
      setIsFirstBooking(false);
    }
  };

  // Complete booking with automatic confirmation email - now uses orchestration for calendar creation
  const completeFirstBookingWithoutEmail = async (forceTalentOverlap = false) => {
    if (!client || isBookingInFlight.current) return;
    isBookingInFlight.current = true;
    setIsFirstBooking(true);
    
    try {
      // First update client with latest form data
      await apiRequest("PATCH", `/api/clients/${client.id}`, {
        ...formData,
        rate: formData.rate || undefined,
        // Task #112 — canonical YYYY-MM-DD straight through.
        dateOfEvent: formData.dateOfEvent || undefined,
      });
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      
      const talentId = formData.talentId || defaultTalent?.id;
      const eventOfferingId = formData.eventOfferingId || defaultOffering?.id;
      
      // Use orchestration endpoint for invoice + calendar creation
      const response = await apiRequest("POST", "/api/bookings/orchestrate", {
        clientId: client.id,
        accountName: formData.accountName,
        contactName: formData.contactName || "",
        emailAddress: formData.emailAddress || "",
        phoneNumber: formData.phoneNumber || "",
        mobilePhone: formData.mobilePhone || "",
        address: formData.address || "",
        city: formData.city || "",
        state: formData.state || "",
        zipCode: formData.zipCode || "",
        timezone: (formData as any).timezone || getTimezoneForState(formData.state || ""),
        clientType: (formData as any).clientType || "",
        talentId,
        eventOfferingId,
        eventDetails: formData.eventDetails,
        rate: formData.rate?.replace(/[$,]/g, "") || "0",
        discount: formData.discount || "0",
        dateOfEvent: formData.dateOfEvent,
        eventTime: formData.eventTime || "",
        rep: formData.rep,
        isTicketedEvent: formData.isTicketedEvent,
        isNoFeeShow: formData.isNoFeeShow,
        eventbriteEnabled: formData.isTicketedEvent && !!formData.eventbriteEventId,
        eventbriteEventId: formData.isTicketedEvent && formData.eventbriteEventId
          ? formData.eventbriteEventId
          : null,
        calendarNotes: "",
        skipCalendar: false,
        skipEmailDraft: true,
        forceTalentOverlap,
      });
      const result = await response.json();
      
      queryClientHook.invalidateQueries({ queryKey: ["/api/invoices"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/clients"] });
      
      const invoice = result.data?.invoice;
      toast({ title: "Show Booked!", description: `Invoice #${invoice?.invoiceNumber} created.` });
      
      if (invoice?.id) {
        setPendingInvoiceId(invoice.id);
        
        // Open email composer modal if client has email address
        if (formData.emailAddress) {
          setEmailComposerInvoiceId(invoice.id);
          setShowEmailComposerModal(true);
        }
      }
    } catch (error: any) {
      console.error("Error completing booking:", error);
      const raw: string = error?.message || "";
      if (raw.startsWith("409:")) {
        try {
          const body = JSON.parse(raw.slice(4).trim());
          if (body?.step === "talent_double_booking" && body?.canOverride) {
            setRawDoubleBookConflict({ message: body.message, talentConflicts: body.talentConflicts || [] });
            return;
          }
        } catch {}
      }
      toast({ title: "Error", description: "Couldn't complete the booking. Please try again.", variant: "destructive" });
    } finally {
      isBookingInFlight.current = false;
      setIsFirstBooking(false);
    }
  };

  const handleBookEvent = async () => {
    if (!validateRequiredFields() || !client) return;
    completeFirstBookingWithoutEmail();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const regionName = client ? regions.find((r) => r.id === client.regionId)?.name || "Unknown" : "";

  const formatDateTime = (date: Date | string | null) => {
    if (!date) return "—";
    const s = typeof date === "string" ? date : date.toISOString();
    // Calendar-day strings (YYYY-MM-DD) — parse without tz-shift.
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, mo, d] = s.split("-").map(Number);
      return new Date(y, mo - 1, d).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
      });
    }
    // Timestamps — display in workspace timezone.
    return new Date(date).toLocaleDateString("en-US", {
      timeZone: getWorkspaceTimeZone(),
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatInvoiceDate = (date: Date | string | null) => {
    if (!date) return "—";
    // dateOfEvent is a calendar-day date column (YYYY-MM-DD) — parse without tz-shift.
    const s = typeof date === "string" ? date : (date as Date).toISOString().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, mo, d] = s.split("-").map(Number);
      return new Date(y, mo - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
    return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const hasInvoices = clientInvoices.length > 0;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="w-full sm:max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0">
            <div className="flex items-center justify-between gap-4">
              <DialogTitle className="text-xl">{isNewClient ? "New Client" : "Client Record"}</DialogTitle>
              <div className="flex items-center gap-2">
                {!isNewClient && regionName && <Badge variant="secondary">{regionName}</Badge>}
                {!isNewClient && client?.id && (
                  <CatchMeUpButton
                    entityType="client"
                    entityId={client.id}
                    eventCount={clientActivities.length}
                  />
                )}
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-6 py-4 overflow-y-auto flex-1">
            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Contact Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="accountName">Account Name</Label>
                  <Input
                    id="accountName"
                    value={formData.accountName}
                    onChange={(e) => handleInputChange("accountName", e.target.value)}
                    placeholder="Business name"
                    data-testid="input-account-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactName">Contact Name</Label>
                  <Input
                    id="contactName"
                    value={formData.contactName}
                    onChange={(e) => handleInputChange("contactName", e.target.value)}
                    placeholder="Primary contact"
                    data-testid="input-contact-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <Input
                    id="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                    placeholder="(555) 123-4567"
                    data-testid="input-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobilePhone">Mobile Phone</Label>
                  <Input
                    id="mobilePhone"
                    value={formData.mobilePhone}
                    onChange={(e) => handleInputChange("mobilePhone", e.target.value)}
                    placeholder="(555) 123-4567"
                    data-testid="input-mobile-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emailAddress">Email Address</Label>
                  <Input
                    id="emailAddress"
                    type="email"
                    value={formData.emailAddress}
                    onChange={(e) => handleInputChange("emailAddress", e.target.value)}
                    placeholder="email@example.com"
                    data-testid="input-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="clientType">Client Type</Label>
                  <Select
                    value={formData.clientType}
                    onValueChange={(value) => handleInputChange("clientType", value)}
                  >
                    <SelectTrigger id="clientType" data-testid="select-client-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientTypeOptions.map((ct) => (
                        <SelectItem key={ct.id} value={ct.name}>{ct.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <ClientContactsSection clientId={client?.id} />
            </div>

            <Separator />

            {/* Address */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Address
                </h3>
              </div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="address">Street Address <span className="text-destructive">*</span></Label>
                  <AddressAutocomplete
                    value={formData.address}
                    onChange={(value) => handleInputChange("address", value)}
                    onAddressSelect={(components) => {
                      const newTimezone = getTimezoneForState(components.state);
                      setFormData(prev => ({
                        ...prev,
                        address: components.address,
                        city: components.city,
                        state: components.state,
                        zipCode: components.zipCode,
                        timezone: newTimezone,
                      }));
                    }}
                    placeholder="Start typing address..."
                    className="h-9 border rounded-md px-3"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="city">City <span className="text-destructive">*</span></Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleInputChange("city", e.target.value)}
                      placeholder="City"
                      data-testid="input-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State <span className="text-destructive">*</span></Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => handleInputChange("state", e.target.value)}
                      placeholder="State"
                      data-testid="input-state"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zipCode">Zip Code <span className="text-destructive">*</span></Label>
                    <Input
                      id="zipCode"
                      value={formData.zipCode}
                      onChange={(e) => handleInputChange("zipCode", e.target.value)}
                      placeholder="12345"
                      data-testid="input-zipcode"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Booking */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Booking
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Talent</Label>
                  <Select
                    value={formData.talentId || ""}
                    onValueChange={(value) => {
                      const talentOfferings = eventOfferings.filter(o => o.talentId === value);
                      const defaultOff = talentOfferings.find(o => o.isDefault) 
                        || talentOfferings.find(o => o.name.toLowerCase().includes("full show"))
                        || talentOfferings[0];
                      handleInputChange("talentId", value);
                      handleInputChange("eventOfferingId", defaultOff?.id || "");
                      handleInputChange("eventDetails", defaultOff?.name || "");
                      handleInputChange("rate", defaultOff?.rate || "0");
                    }}
                  >
                    <SelectTrigger data-testid="select-talent">
                      <SelectValue placeholder="Select talent" />
                    </SelectTrigger>
                    <SelectContent>
                      {talent.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} {e.isDefault && "(Default)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Event Type</Label>
                  <Select
                    value={formData.eventOfferingId || ""}
                    onValueChange={(value) => {
                      const offering = filteredOfferings.find(o => o.id === value);
                      handleInputChange("eventOfferingId", value);
                      handleInputChange("eventDetails", offering?.name || "");
                      setSelectedOfferingIdForPricing(value);
                      // "No Set Rate" offerings: clear rate so the user MUST enter one
                      if ((offering as any)?.noSetRate) {
                        handleInputChange("rate", "");
                      } else {
                        // Use dynamic pricing if available, otherwise use base rate
                        const rate = offering?.hasDynamicPricing
                          ? getDynamicRate(formData.regionId || client?.regionId, formData.clientType, offering, mainFormMarketPricing)
                          : (offering?.rate || "0");
                        handleInputChange("rate", formData.isTicketedEvent || formData.isNoFeeShow ? "0" : rate);
                      }
                    }}
                  >
                    <SelectTrigger data-testid="select-show-details">
                      <SelectValue>{formData.eventDetails || "Select event type"}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {filteredOfferings.length === 0 ? (
                        <SelectItem value="_none" disabled>
                          {formData.talentId ? "No offerings for this talent" : "Select an talent first"}
                        </SelectItem>
                      ) : (
                        filteredOfferings.map((offering) => (
                          <SelectItem key={offering.id} value={offering.id}>
                            {offering.name} - ${parseFloat(offering.rate || "0").toFixed(2)}
                            {offering.hasDynamicPricing && " (Custom)"}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <DollarSign className="w-3 h-3" />
                      Rate
                    </Label>
                    {(formData.isTicketedEvent || formData.venueTicketSplit || formData.venueGuarantee || formData.venueBuyout || formData.venueRental || (selectedOffering as any)?.noSetRate) ? (
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.rate}
                        onChange={(e) => handleInputChange("rate", e.target.value)}
                        placeholder="0.00"
                        data-testid="input-rate"
                      />
                    ) : (
                      <div className="h-9 px-3 flex items-center text-sm font-medium bg-muted/50 rounded-md border" data-testid="text-rate">
                        ${parseFloat(formData.rate || "0").toFixed(2)}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="discount" className="flex items-center gap-2">
                      Discount
                    </Label>
                    <Input
                      id="discount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.discount}
                      onChange={(e) => handleInputChange("discount", e.target.value)}
                      placeholder="0.00"
                      data-testid="input-discount"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      Total
                    </Label>
                    <div className="h-9 px-3 flex items-center text-sm font-semibold text-primary bg-primary/10 rounded-md border border-primary/20" data-testid="text-total">
                      ${Math.max(0, parseFloat(formData.rate || "0") - parseFloat(formData.discount || "0")).toFixed(2)}
                    </div>
                  </div>
                </div>
                
                {/* Ticketed Event / No Fee checkboxes */}
                <div className="flex flex-col gap-3 mt-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="mainTicketedEvent"
                      checked={formData.isTicketedEvent}
                      onCheckedChange={(checked) => {
                        setFormData(prev => ({
                          ...prev,
                          isTicketedEvent: !!checked,
                          isNoFeeShow: checked ? false : prev.isNoFeeShow,
                        }));
                      }}
                      data-testid="checkbox-main-ticketed-event"
                    />
                    <Label htmlFor="mainTicketedEvent" className="text-sm cursor-pointer">
                      Ticketed Event - settlement after event
                    </Label>
                  </div>
                  {formData.isTicketedEvent && (
                    <>
                      <EventbriteEventPicker
                        value={formData.eventbriteEventId}
                        onChange={(id) => setFormData(prev => ({ ...prev, eventbriteEventId: id }))}
                      />
                      {formData.eventbriteEventId && (
                        <div
                          className="ml-6 text-xs rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200"
                          data-testid="text-eventbrite-settlement-mode"
                        >
                          Settlement-later via Eventbrite — rate is held at $0 / TBD until
                          Eventbrite confirms the deposit, then the booking auto-flips to
                          paid at the NET payout amount.
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="mainNoFeeShow"
                      checked={formData.isNoFeeShow}
                      onCheckedChange={(checked) => {
                        setFormData(prev => ({
                          ...prev,
                          isNoFeeShow: !!checked,
                          isTicketedEvent: checked ? false : prev.isTicketedEvent,
                          rate: checked ? "0" : prev.rate,
                        }));
                      }}
                      data-testid="checkbox-main-no-fee-show"
                    />
                    <Label htmlFor="mainNoFeeShow" className="text-sm cursor-pointer">
                      No fee - comp / promo event
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="mainRequiresDocuments"
                      checked={formData.requiresDocuments}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requiresDocuments: !!checked }))}
                      data-testid="checkbox-requires-documents"
                    />
                    <Label htmlFor="mainRequiresDocuments" className="text-sm cursor-pointer">
                      Form / Contract required
                    </Label>
                  </div>
                  {depositsEnabledByAdmin && (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="mainRequiresDeposit"
                          checked={
                            depositForcedByAdmin
                              ? true
                              : !!(formData as any).depositRequired
                          }
                          disabled={depositForcedByAdmin}
                          onCheckedChange={(checked) => {
                            const next = depositForcedByAdmin ? true : !!checked;
                            const rateNum = parseFloat(formData.rate || "0");
                            const defaultAmount =
                              depositMode === "flat"
                                ? (depositFlatCents / 100).toFixed(2)
                                : isFinite(rateNum)
                                ? ((rateNum * depositPercent) / 100).toFixed(2)
                                : "";
                            setFormData((prev) => ({
                              ...prev,
                              depositRequired: next,
                              depositAmount: next
                                ? (prev as any).depositAmount || defaultAmount
                                : "",
                            }));
                          }}
                          data-testid="checkbox-requires-deposit"
                        />
                        <Label
                          htmlFor="mainRequiresDeposit"
                          className="text-sm cursor-pointer"
                        >
                          Deposit required
                          {depositForcedByAdmin && (
                            <span className="ml-1 text-xs text-muted-foreground">
                              (locked by admin)
                            </span>
                          )}
                        </Label>
                      </div>
                      {(depositForcedByAdmin ||
                        (formData as any).depositRequired) && (
                        <div className="flex items-center gap-2 pl-6">
                          <Label
                            htmlFor="mainDepositAmount"
                            className="text-xs text-muted-foreground"
                          >
                            Amount $
                          </Label>
                          <Input
                            id="mainDepositAmount"
                            type="number"
                            min="0"
                            step="0.01"
                            value={(formData as any).depositAmount || ""}
                            disabled={depositForcedByAdmin && depositMode === "flat"}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                depositAmount: e.target.value,
                              }))
                            }
                            className="h-8 w-32"
                            data-testid="input-deposit-amount"
                          />
                          <span className="text-xs text-muted-foreground">
                            {depositMode === "percent"
                              ? `Default ${depositPercent}% of rate`
                              : `Default $${(depositFlatCents / 100).toFixed(2)}`}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* Schedule */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Schedule
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dateOfEvent" className="flex items-center gap-2">
                    <Calendar className="w-3 h-3" />
                    Date of Event <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="dateOfEvent"
                    type="date"
                    value={formData.dateOfEvent}
                    onChange={(e) => handleInputChange("dateOfEvent", e.target.value)}
                    data-testid="input-date-of-show"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="eventTime" className="flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    Event Time <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="eventTime"
                    type="text"
                    value={formData.eventTime}
                    onChange={(e) => handleInputChange("eventTime", e.target.value)}
                    placeholder="e.g. 2:00p"
                    data-testid="input-show-time"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="timezone" className="flex items-center gap-2">
                    <Globe className="w-3 h-3" />
                    Timezone
                  </Label>
                  <Select
                    value={formData.timezone}
                    onValueChange={(value) => handleInputChange("timezone", value)}
                  >
                    <SelectTrigger data-testid="select-timezone">
                      <SelectValue>{TIMEZONE_OPTIONS.find(tz => tz.value === formData.timezone)?.label || formData.timezone}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Venue Details Section — Task #687: smart-collapse. */}
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => {
                  setVenueUserToggled(true);
                  setVenueOpen((v) => !v);
                }}
                className="flex items-center gap-2 text-left -ml-1 px-1 py-0.5 rounded hover:bg-muted/40"
                aria-expanded={venueOpen}
                aria-controls="venue-details-section"
                data-testid="toggle-venue-details"
              >
                {venueOpen ? (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                )}
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Venue Details
                </h3>
              </button>
              {venueOpen && (
              <div id="venue-details-section" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="venueCapacity">Capacity</Label>
                  <Input
                    id="venueCapacity"
                    type="text"
                    inputMode="numeric"
                    value={formData.venueCapacity}
                    onChange={(e) => handleInputChange("venueCapacity", e.target.value)}
                    placeholder="e.g. 250"
                    data-testid="input-venue-capacity"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="venueRentalCost">Rental Cost</Label>
                  <Input
                    id="venueRentalCost"
                    type="text"
                    value={formData.venueRentalCost}
                    onChange={(e) => handleInputChange("venueRentalCost", e.target.value)}
                    placeholder="e.g. $1,500"
                    data-testid="input-venue-rental-cost"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="venueTicketPricing">Ticket Pricing</Label>
                  <Input
                    id="venueTicketPricing"
                    type="text"
                    value={formData.venueTicketPricing}
                    onChange={(e) => handleInputChange("venueTicketPricing", e.target.value)}
                    placeholder="e.g. $25 / $35 VIP"
                    data-testid="input-venue-ticket-pricing"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="venueType">Venue Type</Label>
                <Input
                  id="venueType"
                  type="text"
                  value={formData.venueType}
                  onChange={(e) => handleInputChange("venueType", e.target.value)}
                  placeholder="e.g. intimate historic theater"
                  data-testid="input-venue-type"
                />
              </div>
              {/* Task #698 — Four mutually exclusive venue deal types.
                  Selecting any one unlocks the Rate field. Required when
                  the top-level Ticketed Event flag is checked. */}
              <div className="flex flex-wrap gap-x-6 gap-y-3 pt-1">
                {([
                  ["venueTicketSplit", "Ticket Split", "checkbox-venue-ticket-split"],
                  ["venueGuarantee", "Guarantee", "checkbox-venue-guarantee"],
                  ["venueBuyout", "Buyout", "checkbox-venue-buyout"],
                  ["venueRental", "Rental", "checkbox-venue-rental"],
                ] as const).map(([key, label, testId]) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={(formData as any)[key]}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({
                          ...prev,
                          venueTicketSplit: false,
                          venueGuarantee: false,
                          venueBuyout: false,
                          venueRental: false,
                          [key]: !!checked,
                        }))
                      }
                      data-testid={testId}
                    />
                    <Label htmlFor={key} className="text-sm cursor-pointer">
                      {label}
                    </Label>
                  </div>
                ))}
              </div>
              {formData.isTicketedEvent && !formData.venueTicketSplit && !formData.venueGuarantee && !formData.venueBuyout && !formData.venueRental && (
                <p className="text-xs text-destructive" data-testid="text-venue-deal-required">
                  Pick a settlement structure: Ticket Split, Guarantee, Buyout, or Rental.
                </p>
              )}
              </div>
              )}
            </div>

            <Separator />

            {/* Notes Section */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Notes
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="calendarNotes">Calendar Notes</Label>
                  <Textarea
                    id="calendarNotes"
                    value={formData.calendarNotes}
                    onChange={(e) => handleInputChange("calendarNotes", e.target.value)}
                    placeholder="Notes to include in the calendar event..."
                    rows={2}
                    data-testid="textarea-calendar-notes"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Internal Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange("notes", e.target.value)}
                    placeholder="Internal notes about this client..."
                    rows={2}
                    data-testid="textarea-notes"
                  />
                </div>
              </div>
            </div>

            {/* Invoices List */}
            {clientInvoices.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Invoices ({clientInvoices.length})
                  </h3>
                  <div className="space-y-2">
                    {clientInvoices.map((invoice) => (
                      <div 
                        key={invoice.id} 
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover-elevate"
                        data-testid={`invoice-item-${invoice.invoiceNumber}`}
                        onDoubleClick={() => window.open(`/invoice/${invoice.publicToken || invoice.id}`, '_blank')}
                        title="Double-click to view invoice"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-primary" />
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">Invoice #{invoice.invoiceNumber}</p>
                              <Badge 
                                variant={invoice.status === 'paid' ? 'default' : 'secondary'}
                                className="text-xs"
                              >
                                {invoice.status}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              <p className="text-xs text-muted-foreground">
                                {formatInvoiceDate(invoice.dateOfEvent)} - ${invoice.eventRate || '0'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Documents Section — always visible so files can be uploaded regardless of the "requires documents" checkbox */}
            {client && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                      Documents {clientDocuments.length > 0 ? `(${clientDocuments.length})` : ""}
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => {
                        const input = document.createElement("input");
                        input.type = "file";
                        input.accept = ".pdf,.doc,.docx,.png,.jpg,.jpeg";
                        input.onchange = async (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (!file || !client?.id) return;
                          try {
                            const ct = file.type || "application/octet-stream";
                            const upRes = await fetch(
                              `/api/documents/upload-encrypted?purpose=document_instance`,
                              {
                                method: "POST",
                                credentials: "include",
                                headers: await withCsrfHeader({
                                  "Content-Type": ct,
                                  "x-file-name": file.name,
                                  "x-file-content-type": ct,
                                }),
                                body: file,
                              },
                            );
                            if (!upRes.ok) throw new Error(`upload failed (${upRes.status})`);
                            const upData = await upRes.json();
                            await apiRequest("POST", `/api/clients/${client.id}/upload-document`, {
                              objectKey: upData.objectPath,
                              fileName: file.name,
                              displayName: file.name,
                            });
                            queryClientHook.invalidateQueries({ queryKey: ["/api/clients", client.id, "documents"] });
                            toast({ title: "Document uploaded" });
                          } catch (err) {
                            toast({ title: "Upload failed", variant: "destructive" });
                          }
                        };
                        input.click();
                      }}
                      data-testid="button-upload-document"
                    >
                      <Upload className="w-3 h-3 mr-1" />
                      Upload
                    </Button>
                  </div>
                  {clientDocuments.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No documents yet. Upload one or send from the Documents module.</p>
                  ) : (
                    <div className="space-y-2">
                      {clientDocuments.map((doc: any) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors group"
                          data-testid={`doc-item-${doc.id}`}
                          onDoubleClick={() => openDocumentFile(doc)}
                          title="Double-click to view"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <FileText className="w-4 h-4 text-primary shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{doc.displayName || doc.internalDocumentFileName || "Document"}</p>
                              <p className="text-xs text-muted-foreground">
                                {doc.status === "completed" ? "Completed" : doc.status === "sent" ? "Pending signature" : doc.status}
                                {doc.sentAt ? ` • ${new Date(doc.sentAt).toLocaleDateString()}` : ""}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium mr-1 ${
                              doc.status === "completed" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" :
                              doc.status === "sent" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" :
                              "bg-muted text-muted-foreground"
                            }`}>
                              {doc.status === "completed" ? "Complete" : doc.status === "sent" ? "Pending" : doc.status}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Download"
                              data-testid={`doc-download-${doc.id}`}
                              onClick={(e) => { e.stopPropagation(); openDocumentFile(doc, true); }}
                            >
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Email document"
                              data-testid={`doc-email-${doc.id}`}
                              onClick={(e) => { e.stopPropagation(); setDocToEmail(doc); setDocEmailTo(""); setDocEmailName(""); }}
                            >
                              <Mail className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                              title="Delete document"
                              data-testid={`doc-delete-${doc.id}`}
                              onClick={(e) => { e.stopPropagation(); setDocToDelete(doc); }}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Task #405 — Meetings (Google Meet transcripts + AI notes) */}
            {client && (
              <>
                <Separator />
                <ClientMeetingsSection clientId={client.id} />
              </>
            )}

            {/* Metadata - only show for existing clients */}
            {client && (
              <div className="flex items-center justify-between text-xs text-muted-foreground pt-2">
                <span>Date Booked: {formatDateTime(client.dateBooked)}</span>
                <span>Last Updated: {formatDateTime(client.updatedAt)}</span>
              </div>
            )}
          </div>

          {/* Actions — sticky footer */}
          <div className="flex items-center justify-between gap-4 pt-4 border-t border-border shrink-0">
            <Button variant="outline" onClick={onClose} data-testid="button-close-dialog">
              Close
            </Button>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {isNewClient ? (
                <Button
                  onClick={handleSave}
                  disabled={createClientMutation.isPending}
                  data-testid="button-create-client"
                >
                  {createClientMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Create Client
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={handleSave}
                    disabled={updateClientMutation.isPending}
                    data-testid="button-save-client"
                  >
                    {updateClientMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save Changes
                  </Button>
                  {hasInvoices ? (
                    <div className="flex items-center gap-2">
                      {clientInvoices.length >= 100 && (
                        <span className="text-xs text-muted-foreground">Max 100 shows reached</span>
                      )}
                      <Button
                        variant="secondary"
                        onClick={() => setShowAddEventDialog(true)}
                        disabled={clientInvoices.length >= 100}
                        data-testid="button-book-another-show"
                        title={clientInvoices.length >= 100 ? "Maximum of 100 shows per client reached" : undefined}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Book Another Show
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={handleBookEvent}
                      disabled={isLoadingFirstBookingPreview || isFirstBooking}
                      data-testid="button-book-show-invoice"
                    >
                      {(isLoadingFirstBookingPreview || isFirstBooking) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      <FileText className="w-4 h-4 mr-2" />
                      Book Show
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {client && (
        <AddEventDialog
          isOpen={showAddEventDialog}
          onClose={() => setShowAddEventDialog(false)}
          client={client}
          userId={userId}
          onSuccess={(invoiceId) => {
            queryClientHook.invalidateQueries({ queryKey: ["/api/invoices"] });
            // Open email dialog if we have an invoice ID and client has email
            if (invoiceId && formData.emailAddress) {
              setPendingInvoiceId(invoiceId);
              setShowEmailDialog(true);
            }
          }}
        />
      )}

      {pendingInvoiceId && (
        <ConfirmationEmailDialog
          isOpen={showEmailDialog}
          onClose={() => {
            const invoiceIdToRedirect = pendingInvoiceId;
            setShowEmailDialog(false);
            setPendingInvoiceId(null);
            onClose();
            setLocation(`/invoices?id=${invoiceIdToRedirect}`);
          }}
          invoiceId={pendingInvoiceId}
          onSuccess={() => {
            const invoiceIdToRedirect = pendingInvoiceId;
            queryClientHook.invalidateQueries({ queryKey: ["/api/invoices"] });
            setShowEmailDialog(false);
            setPendingInvoiceId(null);
            onClose();
            setLocation(`/invoices?id=${invoiceIdToRedirect}`);
          }}
        />
      )}

      {/* First Booking Email Confirmation Dialog */}
      <Dialog open={showFirstBookingEmailDialog} onOpenChange={(open) => {
        if (!open && !isFirstBooking) {
          setShowFirstBookingEmailDialog(false);
        }
      }}>
        <DialogContent className="w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Confirm Show & Send Email
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>To</Label>
              <Input 
                value={firstBookingRecipientEmail} 
                onChange={(e) => setFirstBookingRecipientEmail(e.target.value)}
                placeholder="Email address"
                data-testid="input-first-booking-email-recipient"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input 
                value={firstBookingEmailSubject} 
                onChange={(e) => setFirstBookingEmailSubject(e.target.value)}
                placeholder="Email subject"
                data-testid="input-first-booking-email-subject"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Message</Label>
                <VoiceToTextButton onTranscript={(t) => firstBookingEmailBodyEditorRef.current?.insertAtCursor(t)} />
              </div>
              <RichTextEditor
                ref={firstBookingEmailBodyEditorRef}
                value={firstBookingEmailBody}
                onChange={setFirstBookingEmailBody}
                placeholder="Email body"
                minHeight="200px"
                showMergeTokens={true}
              />
            </div>
          </div>
          
          <div className="flex items-center justify-between pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setShowFirstBookingEmailDialog(false)}
              disabled={isFirstBooking}
            >
              Cancel
            </Button>
            <Button 
              onClick={completeFirstBookingFlow}
              disabled={isFirstBooking || !firstBookingRecipientEmail}
              data-testid="button-confirm-first-booking"
            >
              {isFirstBooking && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <PartyPopper className="w-4 h-4 mr-2" />
              Confirm Show
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Composer Modal for sending confirmation after booking */}
      {emailComposerInvoiceId && (
        <EmailComposerModal
          isOpen={showEmailComposerModal}
          onClose={() => {
            setShowEmailComposerModal(false);
            setEmailComposerInvoiceId(null);
          }}
          invoiceId={emailComposerInvoiceId}
          onEmailSent={() => {
            setShowEmailComposerModal(false);
            setEmailComposerInvoiceId(null);
          }}
        />
      )}

      {/* Duplicate Client Confirmation Dialog */}
      <AlertDialog open={duplicateConfirmOpen} onOpenChange={setDuplicateConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate Client Found</AlertDialogTitle>
            <AlertDialogDescription>
              A client with the account name "{formData.accountName}" already exists. Are you sure you want to create another client with the same name?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-duplicate-client">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmedClientCreate}
              data-testid="button-confirm-duplicate-client"
            >
              Create Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Calendar Save Failure — "Save anyways / Cancel" blocking dialog */}
      {calendarSaveFailure && (
        <AlertDialog open={true} onOpenChange={() => {}}>
          <AlertDialogContent data-testid="dialog-calendar-save-failed">
            <AlertDialogHeader>
              <AlertDialogTitle>Calendar Unavailable</AlertDialogTitle>
              <AlertDialogDescription>
                <strong>{calendarSaveFailure.talentName}</strong>'s Google Calendar
                connection is no longer valid — the token was revoked or expired.
                <br /><br />
                You can <strong>save anyway</strong> (the client record will be updated,
                but the calendar event will not be changed) or <strong>cancel</strong>
                to discard this save. You can reconnect the calendar in{" "}
                <em>Admin Panel → Manage Talent → Connect Calendar</em>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                data-testid="button-calendar-save-cancel"
                onClick={() => setCalendarSaveFailure(null)}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="button-calendar-save-anyways"
                onClick={() => {
                  const pending = calendarSaveFailure.pendingUpdates;
                  setCalendarSaveFailure(null);
                  updateClientMutation.mutate({ ...pending, skipCalendarSync: true } as any);
                }}
              >
                Save anyways
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Delete Document Confirmation */}
      <AlertDialog open={!!docToDelete} onOpenChange={(open) => { if (!open) setDocToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this document? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-doc">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => docToDelete && deleteDocMutation.mutate(docToDelete.id)}
              disabled={deleteDocMutation.isPending}
              data-testid="button-confirm-delete-doc"
            >
              {deleteDocMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Document Dialog */}
      <Dialog open={!!docToEmail} onOpenChange={(open) => { if (!open) { setDocToEmail(null); setDocEmailTo(""); setDocEmailName(""); setDocEmailCc(""); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Email Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Send <span className="font-medium text-foreground">{docToEmail?.displayName || docToEmail?.internalDocumentFileName || "this document"}</span> as an email attachment.
            </p>
            <div className="space-y-2">
              <Label htmlFor="doc-email-name">Recipient Name (optional)</Label>
              <Input
                id="doc-email-name"
                value={docEmailName}
                onChange={(e) => setDocEmailName(e.target.value)}
                placeholder="e.g. John Smith"
                data-testid="input-doc-email-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="doc-email-to">Recipient Email</Label>
              <Input
                id="doc-email-to"
                type="email"
                value={docEmailTo}
                onChange={(e) => setDocEmailTo(e.target.value)}
                placeholder="email@example.com"
                data-testid="input-doc-email-to"
              />
            </div>
            <CcEmailInput value={docEmailCc} onChange={setDocEmailCc} data-testid="input-doc-email-cc" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setDocToEmail(null); setDocEmailTo(""); setDocEmailName(""); setDocEmailCc(""); }} data-testid="button-cancel-doc-email">
              Cancel
            </Button>
            <Button
              onClick={() => docToEmail && emailDocMutation.mutate({ docId: docToEmail.id, toEmail: docEmailTo, toName: docEmailName, cc: docEmailCc || undefined })}
              disabled={!docEmailTo || emailDocMutation.isPending}
              data-testid="button-send-doc-email"
            >
              {emailDocMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Send className="w-4 h-4 mr-2" />
              Send Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {rawDoubleBookConflict && (
        <AlertDialog open={true} onOpenChange={(o) => { if (!o) setRawDoubleBookConflict(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Double Booking Detected</AlertDialogTitle>
              <AlertDialogDescription>{rawDoubleBookConflict.message}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setRawDoubleBookConflict(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="button-force-book-confirm-raw"
                onClick={() => {
                  setRawDoubleBookConflict(null);
                  completeFirstBookingWithoutEmail(true);
                }}
              >
                Book Anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}

// ── Task #405 — Meetings (Google Meet transcripts + AI notes) on a client record ──
function ClientMeetingsSection({ clientId }: { clientId: string }) {
  const { data: meetings = [], isLoading } = useQuery<MeetingRecord[]>({
    queryKey: ["/api/clients", clientId, "meetings"],
    enabled: !!clientId,
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-3">
      <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
        Meetings {meetings.length > 0 ? `(${meetings.length})` : ""}
      </h3>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading meetings…
        </div>
      ) : meetings.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="text-no-client-meetings">
          No meetings yet. Schedule a calendar meeting with a Google Meet link to capture transcripts and AI notes.
        </p>
      ) : (
        <div className="space-y-2">
          {meetings.map((m) => (
            <MeetingCard key={m.id} meeting={m} />
          ))}
        </div>
      )}
    </div>
  );
}
